/**
 * Unified workspace configuration — `skaile.yaml` / `<name>.skaile.yaml`
 *
 * Single file combining AI resources, data connectors, agent behavior,
 * runtime defaults, and workspace layout.
 *
 * ── File naming ──────────────────────────────────────────────────────────
 *
 *   skaile.yaml              → canonical default workspace config
 *   staging.skaile.yaml      → alternative workspace config
 *   *.skaile.yaml            → glob pattern for named configs
 *
 * ── Stacking ─────────────────────────────────────────────────────────────
 *
 * Files stack across scope levels (same name must match):
 *   ~/.skaile/skaile.yaml            priority 10 (user)
 *   /app/skaile.yaml                 priority 20 (app)
 *   /project/skaile.yaml             priority 30 (project)
 *
 * ── Relationship to other files ──────────────────────────────────────────
 *
 * .skaile/settings.json   User-specific overrides (API keys, personal
 *                          model preferences). Gitignored. Overrides
 *                          workspace config defaults at runtime.
 *
 * workspace.yaml           Scaffold template. Generates skaile.yaml
 *                          at `skaile init` time. Never read at runtime.
 *
 * agent.yaml               Agent definition (identity). Referenced from
 *                          skaile.yaml via `agent.definition`.
 */

import { existsSync, mkdirSync, readdirSync, readFileSync, writeFileSync } from "node:fs";
import { cp as cpAsync, readdir as readdirAsync, stat as statAsync } from "node:fs/promises";
import { homedir } from "node:os";
import { basename, dirname, join, parse as parsePath, resolve } from "node:path";
import { parse, stringify } from "yaml";
import { globalSkaileDir } from "./cache-paths.js";
import { fsDeadlineMs, tryWithFsDeadline, withFsDeadline } from "./fs-deadline.js";
import {
  type DriverTarget,
  globalBackendRoot,
  SUPPORTED_DRIVER_TARGETS,
} from "./driver-targets.js";
import { fromMcpServerMdContent, parseFrontmatter } from "./manifest.js";
import { type CatalogEntry, parseAssetRef } from "./models.js";
import { getGlobalCacheDir, getRepoCommit, sourceCacheKey } from "./repo-manager.js";
import {
  buildProvenanceIndex,
  type ProvenanceCandidate,
  type ProvenanceIndex,
  type SourceClone,
} from "./walker.js";

// ── Agent config ──────────────────────────────────────────────────────────────

/**
 * Named agent configuration profile. The "default" profile is the active one.
 * Personal overrides (apiKeys, model preference) live in .skaile/settings.json.
 * @docLink packages/core/workspace-config#agent-config-profile
 */
export interface AgentConfigProfile {
  /** Bridge driver to use: "claude-sdk", "codex", or "omp". */
  driver?: string;
  /** Default LLM provider: "anthropic", "openai", "google", etc. */
  provider?: string;
  /**
   * AI cloud transport for claude-sdk: `default | bedrock | vertex | azure |
   * gateway`. Absent means `default` (the provider's native API). Unknown
   * values warn at decode time but never fail — forward compat / tolerated-
   * unknown per the cloud-provider contract.
   *
   * @since 1.3.0
   */
  cloud?: string;
  /**
   * Non-secret transport settings for `cloud` (snake_case — this is the
   * skaile.yaml wire shape; use {@link cloudConfigFromProfile} for the
   * camelCase in-process shape). Secrets NEVER appear here — they ride the
   * `session_init` secrets map.
   *
   * @since 1.3.0
   */
  cloud_config?: { region?: string; project_id?: string; resource?: string; base_url?: string };
  /** Default model identifier. */
  model?: string;
  /** Thinking mode for Claude models. */
  thinking?: "adaptive" | "enabled" | "disabled";
  /** Reasoning effort level. */
  effort?: "low" | "medium" | "high" | string;
  /** Override framework install paths (rarely needed). */
  skills_dir?: string;
  agents_dir?: string;
  prompts_dir?: string;
}

/**
 * Camel-cased in-process shape of an agent profile's `cloud_config` block.
 * Mirrors the bridge's `CloudConfig` (kept structurally identical; no import
 * to avoid a core ↔ bridge cycle).
 *
 * @since 1.3.0
 */
export interface CloudTransportConfig {
  region?: string;
  projectId?: string;
  resource?: string;
  baseUrl?: string;
}

/**
 * Convert a profile's snake_case `cloud_config` wire block to the camelCase
 * in-process shape. The single sanctioned snake→camel conversion site — both
 * the settings cascade and the runner's serve path go through here so the
 * mapping can never drift. Returns `undefined` when nothing is set.
 *
 * @since 1.3.0
 */
export function cloudConfigFromProfile(
  profile: AgentConfigProfile | undefined,
): CloudTransportConfig | undefined {
  const cc = profile?.cloud_config;
  if (!cc || typeof cc !== "object") return undefined;
  const result: CloudTransportConfig = {};
  if (cc.region) result.region = cc.region;
  if (cc.project_id) result.projectId = cc.project_id;
  if (cc.resource) result.resource = cc.resource;
  if (cc.base_url) result.baseUrl = cc.base_url;
  return Object.keys(result).length > 0 ? result : undefined;
}

// ── Startup ───────────────────────────────────────────────────────────────────

/**
 * A startup directive — run on workspace launch.
 *
 * String form:  "agent:research-assistant"  — start the named agent
 * Object form:  { "system-prompt-override": "..." }  — replace system prompt
 *               { "system-prompt-append": "..." }     — append to system prompt
 * @docLink packages/core/workspace-config#startup-directive
 */
export type StartupDirective = string | Record<string, string>;

// ── AI Resources ──────────────────────────────────────────────────────────────

/**
 * An AI resource source with its own dependency list.
 * All declared dependencies are installed from this source's catalog.
 * @docLink packages/core/workspace-config#ai-resource-entry
 */
export interface AiResourceEntry {
  /** Source name (used as cache key and in CLI output). */
  name: string;
  /** Path to local directory or GitHub repo URL. */
  path: string;
  /** Git branch (default: main). */
  branch?: string;
  /** Skill/agent/package/flow refs to install from this source. Syntax: "kind:name". */
  dependencies?: string[];
  /** Auto-deploy to framework dirs on install. */
  auto_deploy?: boolean;
}

// ── Repository Declaration ────────────────────────────────────────────────────

/**
 * Internal-only data shape used by the asset install pipeline (`repo-manager`,
 * `lock`, `runtime-assets`). Carries the resolved URL or path for a `sources[]`
 * entry. Not surfaced directly via the workspace config schema.
 *
 * NOTE: this type was named `RepositoryDeclaration` before 2026-05-31 (the
 * canonical-identity rename). The shape is unchanged — only the name moved
 * from a project-local "repository" concept to the source carrier.
 *
 * @docLink packages/core/workspace-config#source-declaration
 */
export interface SourceDeclaration {
  /** GitHub or git URL (for remote repos). */
  url?: string;
  /** Local filesystem path (for local repos). */
  path?: string;
  /** Git branch (default: main). */
  branch?: string;
  /** Upstream URL for fork workflows. */
  upstream?: string;
  /**
   * Built-in source shipped inside the package (`dist/factory-assets/`). Deploys
   * are **copied**, not symlinked, even though `path` is set: the bundled dir
   * lives under `node_modules` and is replaced on every upgrade, so a symlink
   * into it would dangle. Injected at runtime by `AssetManager`, never persisted
   * to `skaile.yaml`.
   */
  factory?: boolean;
}

// ── Source Entry ──────────────────────────────────────────────────────────────

/**
 * One entry in a project's `sources:` list. Names the upstream git URL plus
 * an optional pin (tag, branch HEAD, or 40-char SHA). The publisher identity
 * is NOT carried here — it is intrinsic to the asset bytes, declared in the
 * source's own `skaile.yaml`.
 *
 * Persisted under the `sources:` key in `skaile.yaml`. Bytes live under
 * `<cache>/sources/<slug>/` (unified machine-global clone cache, via
 * `getGlobalCacheDir()`) where the slug is derived from the URL.
 *
 * @docLink packages/core/workspace-config#source-entry
 */
export interface SourceEntry {
  /** Git URL (ssh or https). Cache key derives from this. */
  url: string;
  /** Optional pin: branch | tag | 40-char sha. Defaults to HEAD of default branch. */
  pin?: string;
}

/**
 * One entry in a project's `stores:` list. Catalog endpoint URL only.
 * The store backend authenticates publisher identity; no project-side
 * trust declaration applies.
 *
 * @docLink packages/core/workspace-config#store-entry
 */
export interface StoreEntry {
  /** Catalog base URL (e.g. `https://skaile.store`). */
  url: string;
}

/**
 * One entry in `overrides:`. Pins a specific canonical ref to one source
 * URL when two or more candidates diverge on sha256. The `reason:` field
 * is REQUIRED and non-empty; empty or missing is a parse-time error.
 *
 * @docLink packages/core/workspace-config#override-entry
 */
export interface OverrideEntry {
  /** Canonical ref form: `<publisher>/<kind>:<name>@<version>`. */
  ref: string;
  /** One of the candidate source URLs. */
  source: string;
  /** Non-empty justification. */
  reason: string;
}

// ── Mounts ──────────────────────────────────────────────────────────────────

/**
 * A filesystem mount — projected storage backend.
 * @docLink packages/core/workspace-config#mount-declaration
 */
interface MountDeclaration {
  /** Unique identifier. Used as mount dir name. */
  id: string;
  /** Mount driver: "local", "git", "s3", "webdav", "sharepoint". */
  driver: string;
  /** What to mount: URL, bucket path, host filesystem path. */
  source: string;
  /** Workspace-relative mount point (default: .mounts/<id>). */
  target?: string;
  /** Access level enforced by the mount driver. */
  access?: "read-only" | "read-write";
  /** Credential reference: "env:VAR_NAME" or inline value. */
  auth?: string;
  /**
   * Whether to watch this mount for filesystem changes.
   * true = always watch, false = never watch, undefined = auto (watch if read-write).
   */
  watch?: boolean;
  /**
   * **Expert-only.** When `true`, the mount manager wires the resolved
   * credential into the workspace so the agent's interactive `git`/`curl`
   * tools can authenticate directly against the mount's host. Defaults to
   * `false` — agents have no credential exposure and rely on driver-managed
   * pulls/pushes. See `_devlog/specs/2026-05-05-git-credential-tiers.md`.
   */
  exposeAccessToken?: boolean;
  /**
   * Optional override for the access token TTL (seconds). When omitted, the
   * provider's natural TTL is used. Values above the provider's documented
   * maximum are silently capped.
   */
  accessTokenTTL?: number;
  /** Driver-specific configuration (branch, region, etc.). */
  options?: Record<string, unknown>;
}

// ── Connectors ────────────────────────────────────────────────────────────────

/**
 * A data connector — tool-accessed backend the agent interacts with.
 * @docLink packages/core/workspace-config#connector-declaration
 */
export interface ConnectorDeclaration {
  /** Unique identifier. Used as tool namespace. */
  id: string;
  /** Connector adapter: "postgres", "redis", "sqlite", "memory", "xstate", "xstate-store", "yjs". */
  driver: string;
  /**
   * Access level enforced by ConnectorManager.
   * Optional in config — ConnectorManager defaults to "read-only" when absent.
   */
  access?: "read-only" | "read-write";
  /** Credential reference: "env:VAR_NAME" or inline value. */
  auth?: string;
  /**
   * Backend-mediated credential routing key. For backend-auth mounts the
   * platform serializes this top-level so the runner can mint/refresh the
   * connector token via `host.refresh_credential`. Must survive the
   * skaile.yaml → declaration mapping or live token refresh fails.
   */
  providerLinkId?: string;
  /**
   * Operating mode for connectors that manage a working copy. Orthogonal to
   * faces. `agent` = the agent drives the source natively (e.g. native git) and
   * the connector only provisions short-lived credentials. `sync` = the
   * connector owns the working copy (clone + watch + auto-commit/pull). Absent =
   * the driver's legacy `options.{session,sync,lifecycle}` blocks decide.
   */
  mode?: "agent" | "sync";
  /** Adapter-specific configuration (dsn, host, etc.). */
  options?: Record<string, unknown>;

  // ── Extended fields (Plan A: first-level asset support) ──────────────────
  /** Semver range for future catalog versioning, e.g. "^1.0.0". */
  version?: string;
  /**
   * Run a health check (ping) when the connector is first connected.
   * Default: false. Set to true to surface connection errors early.
   */
  health_check?: boolean;
  /**
   * Inject this connector's skill description into the agent's system prompt.
   * Default: false.
   */
  expose_as_skill?: boolean;
  /** Tags for catalog browsing and skill routing. */
  tags?: string[];

  /**
   * Optional filesystem face — makes the connector "mountable".
   * When present, ConnectorManager allocates a mount directory and passes it
   * to the connector via `ConnectContext.mountTarget`.
   *
   * YAML key: `mount` (sub-block under a `connectors:` entry).
   * Replaces the legacy top-level `mounts:` key (hard cut — see Task 11).
   */
  mount?: {
    /** Source URL or path (repo URL, bucket name, host path). */
    source: string;
    /** Override mount target directory (default: `.mounts/<id>/`). */
    target?: string;
    /** Watch flag: omitted → watch read-write, skip read-only. */
    watch?: boolean;
    /** Expert-only: expose the resolved credential to the agent CLI. */
    exposeAccessToken?: boolean;
    /** Optional access-token TTL override (seconds). */
    accessTokenTTL?: number;
  };
}

// ── MCP Servers ──────────────────────────────────────────────────────────────

/**
 * Reference to a Nix-built asset recipe.
 *
 * Two source forms:
 *  - **Platform-flake attr (legacy):** `{ attr }` selects an attribute path in
 *    the platform flake (`/etc/skaile/flake/`), e.g. `mcps.excel`, `mcps.ppt`,
 *    `stacks.baseline`. The leaf of `attr` doubles as the recipe-map lookup key.
 *  - **Self-contained flake (BYO-flake):** `{ flake, attr? }` names a flake that
 *    travels with the asset (`flake: "."`) or a remote URL for allowlisted
 *    third-party publishers. The host-side builder resolves it to a store path.
 *    Here `attr` is the in-flake BUILD target only (defaults to `"default"`);
 *    the recipe **id** (the MCP declaration id) is the recipe-map / `${recipe:<id>}`
 *    marker key, never `attr`.
 *
 * Resolution happens at session start (cold) and on `reconfigure_mcps` (hot-add)
 * via the prebuilt recipe map → `nix path-info --offline` fallback.
 *
 * @docLink packages/core/concepts#asset-recipe
 */
export interface AssetRecipe {
  /**
   * Flake attribute to resolve/build.
   * - Legacy `{ attr }` form: required, e.g. `mcps.excel`.
   * - BYO-flake `{ flake, attr? }` form: optional, defaults to `"default"`.
   */
  attr?: string;
  /**
   * Optional flake source. `"."` = the asset's own `flake.nix` (resolved
   * relative to the asset directory by the host-side builder). A flake URL
   * (`github:…`, `git+https://…`, `git+ssh://…`, `path:/…`) is permitted only
   * for publishers on the curated allowlist (enforced host-side). Absent = the
   * platform flake (legacy behaviour).
   */
  flake?: string;
  /**
   * Publisher id that authored this recipe's flake. Required (host-side) when
   * `flake` is a remote URL; the builder checks it against the allowlist.
   * Defaults to the owning asset's publisher when `flake` is `"."`.
   */
  publisher?: string;
}

/** Default in-flake build target for the BYO-flake form when `attr` is omitted. */
export const DEFAULT_RECIPE_ATTR = "default";

/**
 * Validates an `AssetRecipe.flake` source string.
 *
 * Accepted forms:
 *  - `"."` — the asset's own flake directory (always allowed).
 *  - a flake URL with a recognised scheme: `github:`, `git+https://`,
 *    `git+ssh://`, `path:/` (absolute-only).
 *
 * Rejected: empty strings, local relative/absolute paths other than `"."`
 * (including `..` traversal and bare `/abs/path` — those must go through an
 * explicit `path:` URL), and unknown schemes. This guards the value before the
 * host-side builder interpolates it into `nix build "<flake>#<attr>"`; the
 * allowlist gate is a *separate* trust check, not a substitute for this.
 *
 * NOTE: this validates *shape*, not allowlist membership — the publisher
 * allowlist is enforced host-side by the builder, which has the allowlist file.
 *
 * @throws Error when `flake` is malformed
 */
export function validateAssetRecipeFlake(flake: string): void {
  if (typeof flake !== "string" || flake.length === 0) {
    throw new Error("AssetRecipe.flake must be a non-empty string");
  }
  if (flake.length > 500) {
    throw new Error(`AssetRecipe.flake too long (${flake.length} chars, max 500)`);
  }
  if (flake === ".") return;
  // Recognised flake-URL schemes only. Everything else (relative paths, bare
  // absolute paths, `..`, `file:`, arbitrary schemes) is rejected — a local
  // path must be expressed as an explicit `path:` URL.
  // `path:` is absolute-only (`path:/…`): a remote catalog entry must not be
  // able to name a relative path or `..` escape via `path:relative/x`.
  const ALLOWED_SCHEME = /^(github:|git\+https:\/\/|git\+ssh:\/\/|path:\/)/;
  if (!ALLOWED_SCHEME.test(flake)) {
    throw new Error(
      `AssetRecipe.flake "${flake}" has an unsupported source. ` +
        `Allowed: "." or a flake URL (github:, git+https://, git+ssh://, path:/).`,
    );
  }
  // Defense-in-depth: reject shell/nix metacharacters that have no place in a
  // flake ref even within an accepted scheme (the ref is also passed quoted by
  // the builder, but belt-and-braces). `#` is the nix attr separator — the
  // builder appends `#<attr>`, so a `#` in the flake ref would yield a
  // malformed `flake#x#attr` invocation.
  if (/[\s`$;|&<>(){}#\n\r]/.test(flake)) {
    throw new Error(`AssetRecipe.flake "${flake}" contains forbidden characters.`);
  }
}

/**
 * Validates an `AssetRecipe.attr` value against nix attribute path syntax.
 *
 * Nix attribute paths are dot-separated segments. Each segment must start with
 * a letter and contain only letters, digits, underscores, hyphens, and dots.
 * Characters like `#`, spaces, and nix expression fragments are explicitly
 * rejected — even though `spawnSync` array args prevent shell injection, nix
 * parses the combined `flakeRef#attr` string and invalid chars trigger
 * unintended evaluation paths.
 *
 * Deliberate restriction: leading digits per segment are not allowed even
 * though some nix attrs permit them, because they are rare in practice and
 * the restriction makes the validation straightforward to audit.
 *
 * @throws Error when `attr` is invalid
 */
export function validateAssetRecipeAttr(attr: string): void {
  if (typeof attr !== "string" || attr.length === 0) {
    throw new Error("AssetRecipe.attr must be a non-empty string");
  }
  if (attr.length > 200) {
    throw new Error(`AssetRecipe.attr too long (${attr.length} chars, max 200)`);
  }
  const VALID_ATTR = /^[a-z][a-z0-9._-]*(?:\.[a-z][a-z0-9._-]*)*$/i;
  if (!VALID_ATTR.test(attr)) {
    throw new Error(
      `AssetRecipe.attr "${attr}" contains invalid characters. ` +
        `Allowed: letters, digits, underscore, hyphen, dot. ` +
        `Each dot-segment must start with a letter.`,
    );
  }
}

/**
 * A declarative external MCP server — spawned or connected at session startup
 * and injected into the Claude SDK driver as a named `mcpServers` entry.
 *
 * Maps directly to the Claude Agent SDK's `McpServerConfig` union:
 *   - `transport: "stdio"` (default) → `McpStdioServerConfig`  — subprocess spawn
 *   - `transport: "sse"`             → `McpSSEServerConfig`     — HTTP Server-Sent Events
 *   - `transport: "http"`            → `McpHttpServerConfig`    — HTTP streaming
 *
 * The `id` becomes the key in the SDK's `mcpServers` record and also the MCP
 * tool prefix (e.g., `mcp__my-server__<tool-name>`).
 * @docLink packages/core/workspace-config#mcp-server-declaration
 */
export interface McpServerDeclaration {
  /** Unique identifier — used as the `mcpServers` record key and tool prefix. */
  id: string;
  /**
   * Transport type.
   * Default: `"stdio"` (subprocess).
   */
  transport?: "stdio" | "sse" | "http";
  /** For `"stdio"`: the command to execute (e.g. `"npx"`, `"node"`, `"python"`). */
  command?: string;
  /** For `"stdio"`: arguments passed to the command. */
  args?: string[];
  /** For `"stdio"`: environment variables injected into the subprocess. */
  env?: Record<string, string>;
  /** For `"sse"` and `"http"`: the server URL. */
  url?: string;
  /** For `"sse"` and `"http"`: HTTP headers (e.g. `Authorization`). */
  headers?: Record<string, string>;
  /**
   * Credential resolution mode for a remote (sse/http) server. `"backend"` means
   * the platform mints + provisions the bearer (header `Authorization:
   * env:MCP__<id>__AUTH`) and the runner re-mints it on a 401. Written by the
   * platform; absent for local / static-token servers.
   */
  auth?: string;
  /**
   * For a remote `auth: "backend"` server: the org `ProviderLink` whose OAuth
   * credential backs this server's bearer. The runner passes it as the re-mint
   * `id` on a 401. Written by the platform alongside `auth`.
   */
  providerLinkId?: string;
  /** Short description of what this server provides (used in catalog). */
  description?: string;
  /** Tags for catalog browsing and discovery. */
  tags?: string[];
  /**
   * Named launcher strategy for a local (stdio) server. Selectable + explicit;
   * default is inferred (recipe present → "recipe"; else a pass-through
   * "path"/"package-runner" command-shape). Only "recipe" carries resolution
   * logic; "package-runner" (npx/uvx/pip) and "path" are documented command
   * shapes the stdio transport already spawns as-is. Ignored for remote (sse/http).
   */
  launcher?: "recipe" | "package-runner" | "path";
  /**
   * Optional Nix recipe binding. When present, the runner resolves the
   * recipe's `/nix/store` out-path before subprocess spawn and substitutes
   * `${recipe:<id>}` / `${recipe:<id>:<sub>}` / `${recipe:<id>:bin}` /
   * `${recipe:<id>:lib}` markers in `command`, `args`, and `env` values.
   * `<id>` matches this declaration's `id` field.
   */
  recipe?: AssetRecipe;
  /** Owner-authored instruction text from the materialized MCP.md body (preset custom instructions). */
  instructions?: string;
  /**
   * Internal provenance, never authored in `skaile.yaml`: set when a sibling
   * `.instance.json` contributed **resolved secret values** to `env` (see
   * `foldInstanceEnv`). Those values are live plaintext credentials that must
   * stay in memory — the same reason `.instance.json` is never copied into
   * `.claude/skills/`. Any consumer that would persist a declaration to disk
   * (e.g. rendering it into a coding agent's MCP config) must skip a
   * declaration carrying this flag.
   */
  instanceSecretsFolded?: boolean;
}

// ── Agent Configuration ──────────────────────────────────────────────────────

/**
 * Platform-level context describing the environment the agent operates in.
 * Rendered by the runner into a "## Platform" section of the system prompt.
 */
export interface AgentContextConfig {
  /** Platform identifier (e.g. "skaile"). Controls the rendered header sentence. */
  platform?: string;
  /** Whether multiple users may interact in the same session. */
  multi_user?: boolean;
  /** Session persistence model. */
  session_model?: "persistent" | "ephemeral";
}

export interface AgentConfig {
  /**
   * Agent definition reference. Resolved in order:
   *   - Local path:        ".skaile/agent" or "./my-agent"
   *   - Catalog reference: "agent:<name>" (resolved via asset manager catalog)
   *   - Resource path:     "ai-assets://<domain>/agents/<name>"
   */
  definition?: string;

  /**
   * Platform-level context (platform identifier, multi-user flag, session model).
   * Consumed by the runner to build the "## Platform" section of the system prompt.
   */
  context?: AgentContextConfig;

  /** Agent runtime constraints. */
  permissions?: AgentPermissions;

  /** Lifecycle hooks — shell commands run at specific points. */
  hooks?: AgentHooks;

  /** Named subagent definitions available for delegation. */
  subagents?: Record<string, SubagentConfig>;

  /**
   * Control which framework fragments are included in the rendered system prompt.
   *   true  (default) — include built-in fragment
   *   false           — omit this fragment
   *   string          — path to a custom markdown file (relative to project root)
   *
   * Fragment IDs: "agent-mode" | "skill-discovery" | "connector-usage" | "handoff"
   */
  fragments?: Record<string, boolean | string>;

  /**
   * Additional markdown files appended at the end of every rendered agent system
   * prompt in this workspace. Paths relative to the project root (skaile.yaml).
   *
   * YAML key: `prompt-extensions`
   */
  "prompt-extensions"?: string[];

  /**
   * Free-form user-authored agent prompt. Plain text or markdown — no length
   * cap, no template substitution. Prepended immediately after the platform
   * context section and before the environment section in the assembled
   * system prompt (see runner's {@link assembleSystemPrompt}).
   *
   * Written by the platform's wake-time yaml serializer from the combined
   * project-level + session-level prompts stored on `SkaileConfigData.agent.prompt`.
   * Standalone CLI / forge sessions may set this directly in `skaile.yaml`.
   *
   * Spec: `docs/superpowers/specs/2026-05-13-platform-agent-prompt-design.md`
   * @since 2026-05
   */
  prompt?: string;
}

export interface AgentPermissions {
  /** Maximum agentic turns per query (default: 15). */
  max_turns?: number;
  /**
   * Permission mode for Claude SDK driver.
   * 'auto' = bypassPermissions (default for automated flows).
   * 'interactive' = prompt for each tool use.
   */
  permission_mode?: "auto" | "interactive";
  /** Glob patterns the agent may NOT write to. Stacking: union across scopes. */
  deny_write?: string[];
  /** Glob patterns the agent may NOT read. Stacking: union across scopes. */
  deny_read?: string[];
  /**
   * Network egress policy. Codec is permissive — the platform decides
   * enforcement.
   */
  network?: NetworkPolicy;
}

/**
 * Per-session egress policy: `open` allows everything, `off` allows only
 * platform-resolved LLM-provider endpoints, `allowlist` adds user-supplied
 * domains. `allowlist` field is ignored when `mode !== 'allowlist'`.
 */
export interface NetworkPolicy {
  mode: "open" | "off" | "allowlist";
  allowlist?: string[];
}

const KNOWN_NETWORK_MODES = ["open", "off", "allowlist"] as const;

export interface AgentHooks {
  /** Run before a flow starts (after resources are connected). */
  pre_flow?: HookEntry[];
  /** Run after a flow completes (before resources disconnect). */
  post_flow?: HookEntry[];
  /** Run before each flow node executes. */
  pre_node?: HookEntry[];
  /** Run after each flow node completes successfully. */
  post_node?: HookEntry[];
}

export interface HookEntry {
  /** Human-readable hook name. */
  name: string;
  /** Shell command to execute. */
  run: string;
  /** Working directory relative to workspace root (default: workspace root). */
  cwd?: string;
  /** Timeout in seconds (default: 60). */
  timeout?: number;
  /** Continue flow if hook fails (default: false). */
  continue_on_error?: boolean;
}

export interface SubagentConfig {
  /** What this subagent does. */
  description: string;
  /** System prompt for the subagent. */
  prompt: string;
  /** Tools this subagent may use (default: all). */
  tools?: string[];
  /** Tools this subagent may NOT use. */
  disallowed_tools?: string[];
  /** Model override. */
  model?: string;
}

// ── Workspace Layout ─────────────────────────────────────────────────────────

export interface WorkspaceLayoutConfig {
  /** Directories to ensure exist (created at scaffold time, verified at run time). */
  directories?: string[];

  /** Git configuration. */
  git?: GitConfig;

  /** Post-scaffold setup commands (run once after `skaile init`). */
  setup?: SetupEntry[];

  /** Container configuration (Docker). */
  container?: ContainerConfig;
}

export interface GitConfig {
  /** Initialize a git repo at scaffold time (default: true). */
  init?: boolean;
  /** .gitignore entries. Stacking: concatenate + deduplicate. */
  ignore?: string[];
}

export interface SetupEntry {
  /** Human-readable name. */
  name: string;
  /** Shell command to execute. */
  run: string;
  /** Working directory relative to workspace root. */
  cwd?: string;
  /** Continue if command fails (default: false). */
  continue_on_error?: boolean;
}

export interface NixContainerConfig {
  /**
   * Nixpkgs package attribute names to include in the environment.
   * Example: ["nodejs_22", "bun", "git", "python3"]
   */
  packages?: string[];
  /**
   * Nixpkgs registry name used as a package prefix for `nix shell`.
   * MUST be a bare registry name like "nixpkgs" — not a channel path like
   * "nixpkgs/nixos-24.11" (which is not valid as a flake ref prefix).
   * Defaults to "nixpkgs".
   */
  channel?: string;
  /**
   * Path to a Nix file (shell.nix / flake.nix) relative to the project root.
   * When set, `packages` and `channel` are ignored — the file is the source of truth.
   */
  flake?: string;
  /**
   * Predefined named stack to resolve from the system stack registry (SKAILE_NIX_STACK_REGISTRY).
   * Overridden by `packages` if present.
   * Takes precedence over the parent `ContainerConfig.stack` field when nix mode is active.
   */
  stack?: string;
}

export interface ContainerConfig {
  /** Enable Docker container generation. */
  enabled?: boolean;
  /** Base Docker image. */
  image?: string;
  /** System packages to install. */
  packages?: string[];
  /** Agent CLIs to install globally in the container. */
  agent_clis?: string[];
  /** Ports to expose. */
  ports?: string[];
  /** Environment variables to pass through. */
  env?: string[];
  /** Docker volume/bind mounts. */
  mounts?: Array<{ type: "bind" | "volume"; source: string; target: string }>;
  /** WebSocket port for IPC. */
  ws_port?: number;
  /**
   * Named system stack.
   * - Docker mode: resolved to an image tag via `dockerImageMap` (SKAILE_DOCKER_IMAGE_MAP).
   * - Nix mode: resolved to a package list via `nixStackRegistry` (SKAILE_NIX_STACK_REGISTRY).
   *   Overridden by `nix.stack` if both are present.
   */
  stack?: string;
  /** Nix-specific environment configuration (nix session mode only). */
  nix?: NixContainerConfig;
}

// ── Top-level config ──────────────────────────────────────────────────────────

export interface SkWorkspaceConfig {
  /** Project name (defaults to directory basename). */
  name?: string;
  /** Project description. */
  description?: string;

  /**
   * Agent configuration profiles. The "default" profile provides runtime
   * defaults (framework, model, provider). Personal overrides live in
   * .skaile/settings.json and always take priority.
   *
   * YAML key: `agent-config` or `agent_config`
   */
  agent_config?: Record<string, AgentConfigProfile>;

  /**
   * Startup directives — executed when the workspace is launched.
   * Each item is either a string ("agent:name") or a map
   * ({ "system-prompt-override": "..." }).
   */
  startup?: StartupDirective[];

  /**
   * Asset dependencies using `kind:name@<publisher>[#pin]` syntax.
   * Top-level flat list — sources and deps are separate concerns.
   *
   * YAML key: `dependencies`
   */
  dependencies?: string[];

  /**
   * Global cross-backend install set — the coding-agent backends a `skaile
   * install --global` fans `dependencies:` out to. Only meaningful in the
   * user-scope SSOT (`~/.skaile/skaile.yaml`). When absent, the backend set is
   * auto-detected from existing `~/.<backend>` dirs (see `resolveGlobalBackends`).
   * Each entry must be a `DriverTarget` (`claude-code` | `omp` | `codex`).
   *
   * YAML key: `global_backends`
   */
  global_backends?: DriverTarget[];

  /**
   * Github sources this project depends on. Each entry is `{url, pin?}`; the
   * cache slug is derived from the URL. `skaile install` clones any missing
   * entries; `skaile source add/remove/sync` are the CRUD surface.
   *
   * YAML key: `sources`
   */
  sources?: SourceEntry[];

  /**
   * Consumption-half: trusted store catalogs queried per dep.
   *
   * YAML key: `stores`
   */
  stores?: StoreEntry[];

  /**
   * Consumption-half: conflict-resolution overrides with required reason.
   *
   * YAML key: `overrides`
   */
  overrides?: OverrideEntry[];

  /**
   * Local patches applied during install (new format).
   * Maps "kind:name" → patch file path relative to project root.
   *
   * YAML key: `patches`
   */
  patches?: Record<string, string>;

  /**
   * @deprecated The top-level `mounts:` key is no longer supported as of Task 11.
   *
   * Move filesystem-projected storage backends under `connectors:` with a
   * `mount:` sub-block. See `docs/migration-mounts-to-connectors.md`.
   *
   * This field is kept on the type **only** so `config.ts` can detect a
   * present `mounts:` block and throw a migration error. It is never merged,
   * applied, or forwarded to any manager. `normalizeConfig` still populates it
   * when `mounts:` is present in the YAML so the detection check works.
   *
   * YAML key: `mounts` (rejected at runtime — do not use)
   */
  mounts?: MountDeclaration[];

  /**
   * Data connectors — tool-accessed backends.
   *
   * Each declaration's `driver` field is an *implicit* catalog ref
   * (`connector:<driver>`). The runtime resolves it through
   * `resolveRuntimeAssets()`, which scans every declared `repositories` entry
   * plus the implicit built-in `factory-assets` repo for matching `CONNECTOR.md`
   * manifests. Drivers that do not match any catalog entry produce a
   * `missing_driver` warning at session startup.
   *
   * YAML key: `connectors`
   */
  connectors?: ConnectorDeclaration[];

  /**
   * External MCP servers — injected into the Claude SDK driver at session startup.
   * Supports stdio subprocess, SSE, and HTTP transports.
   *
   * YAML key: `mcp_servers`
   */
  mcp_servers?: McpServerDeclaration[];

  /** Agent behavior — definition reference, permissions, hooks, subagents. */
  agent?: AgentConfig;

  /** Workspace layout — directories, git, setup scripts, container. */
  workspace?: WorkspaceLayoutConfig;

  /**
   * Secret provisioning configuration.
   * Controls how connector credentials are resolved at runtime.
   *
   * YAML key: `secrets`
   */
  secrets?: SecretsConfig;

  /**
   * Telemetry configuration — passed through raw to the telemetry package.
   * Parsed by `resolveTelemetryConfig` in `@skaile/workspaces/telemetry`.
   *
   * YAML key: `telemetry`
   */
  telemetry?: Record<string, unknown>;

  /**
   * Session compaction settings -- controls when and how conversation
   * snapshots are created.
   *
   * YAML key: `compaction`
   */
  compaction?: CompactionConfig;

  /**
   * Plugin specs to load into pluginRegistry — npm specifiers like
   * "@skaile/provider-fly@^0.1.0". Opt-in; no auto-discovery. YAML key: `plugins`.
   */
  plugins?: string[];
  /** Deploy target selection. YAML key: `deploy`. */
  deploy?: DeployBlock;
}

/** Deploy target selection for `skaile deploy`. YAML key: `deploy`. Resolved through pluginRegistry at deploy time. */
export interface DeployBlock {
  /** Deploy target id (e.g. "local", "docker", "fly") — resolved against pluginRegistry at deploy time, not validated here. */
  target: string;
  /** Target-specific config; validated by the target's configSchema at resolve time. */
  config?: unknown;
}

export interface SecretsConfig {
  /**
   * How secrets are provided to the container.
   *   "env"         — read from process.env (default, for CLI/standalone)
   *   "provisioned" — wait for secrets over transport bridge (platform containers)
   */
  provider?: "env" | "provisioned";
  /** Timeout in ms for waiting for provisioned secrets (default: 30000). */
  timeoutMs?: number;
}

// ── Compaction config ────────────────────────────────────────────────────────

export interface CompactionConfig {
  /** Enable managed compaction (default: true). */
  enabled?: boolean;
  /** Context fill percentage that triggers compaction (default: 80). */
  thresholdPercent?: number;
  /** Minimum ms between compactions to prevent thrashing (default: 120000). */
  minCooldownMs?: number;
  /** Enable manual compact command in expert mode (default: false). */
  manualCompactEnabled?: boolean;
}

export const COMPACTION_DEFAULTS: Required<CompactionConfig> = {
  enabled: true,
  thresholdPercent: 80,
  minCooldownMs: 120_000,
  manualCompactEnabled: false,
};

// ── Deprecated type aliases ───────────────────────────────────────────────────

/** @deprecated Use AgentConfigProfile */
export interface RuntimeDefaults {
  framework?: string;
  driver?: string;
  provider?: string;
  model?: string;
  skills_dir?: string;
  agents_dir?: string;
  prompts_dir?: string;
}

/** @deprecated Use AiResourceEntry[] */
export interface AiResourcesConfig {
  sources?: AiResourceSource[];
  requires?: string[];
  auto_deploy?: boolean;
}

/** @deprecated Use AiResourceEntry */
export interface AiResourceSource {
  name: string;
  path: string;
  branch?: string;
}

// ── Config file info ──────────────────────────────────────────────────────────

export interface SkWorkspaceConfigFile {
  /** Absolute path to the config file. */
  path: string;
  /** Workspace name (extracted from filename). */
  name: string;
  /** Parsed config. */
  config: SkWorkspaceConfig;
  /**
   * Diagnostics from decoding this file — legacy-shape warnings (camelCase keys,
   * flat agent-config, the old ai_resources object) and unrecognized
   * driver/provider/access values. Absent on configs synthesized in-memory.
   */
  diagnostics?: Diagnostic[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Suffix for named workspace configs: `<name>.skaile.yaml` */
export const SKAILE_YAML_SUFFIX = ".skaile.yaml";
/** Filename for the default workspace config when no name is given. */
export const SKAILE_YAML_DEFAULT = "skaile.yaml";

/** @deprecated Use SKAILE_YAML_SUFFIX */
export const SK_WORKSPACE_SUFFIX = SKAILE_YAML_SUFFIX;
/** @deprecated Use SKAILE_YAML_DEFAULT */
export const SK_WORKSPACE_DEFAULT_NAME = "default";

// ── Config I/O ────────────────────────────────────────────────────────────────

/**
 * Return the canonical filename for a workspace config.
 * The default workspace resolves to `"skaile.yaml"`; named workspaces resolve to
 * `"<name>.skaile.yaml"`.
 *
 * @param name - Optional workspace name (omit or pass `"default"` for the primary config)
 * @returns Filename string (not a full path)
 * @docLink packages/core/workspace-config#workspace-config-filename
 */
export function workspaceConfigFilename(name?: string): string {
  if (!name || name === "default") return SKAILE_YAML_DEFAULT;
  return `${name}${SKAILE_YAML_SUFFIX}`;
}

/**
 * Return `true` if `filename` matches the workspace config naming convention
 * (`"skaile.yaml"` or any `"*.skaile.yaml"`).
 *
 * @param filename - Bare filename (no directory component)
 * @docLink packages/core/workspace-config#is-workspace-config-filename
 */
export function isWorkspaceConfigFilename(filename: string): boolean {
  return filename === SKAILE_YAML_DEFAULT || filename.endsWith(SKAILE_YAML_SUFFIX);
}

/**
 * Extract the workspace name from a config filename.
 * `"skaile.yaml"` → `"default"`, `"staging.skaile.yaml"` → `"staging"`.
 *
 * @param filename - Bare filename produced by `workspaceConfigFilename`
 * @returns Workspace name string
 * @docLink packages/core/workspace-config#workspace-name-from-filename
 */
export function workspaceNameFromFilename(filename: string): string {
  if (filename === SKAILE_YAML_DEFAULT) return "default";
  return filename.slice(0, -SKAILE_YAML_SUFFIX.length);
}

/**
 * Load a single workspace config file from `dir`.
 * When `name` is omitted and no `skaile.yaml` exists, falls back to the sole
 * `*.skaile.yaml` file in the directory if exactly one is present.
 *
 * @param dir - Directory to search for the config file
 * @param name - Optional workspace name (omit for the default `skaile.yaml`)
 * @returns Parsed config file info, or `null` if no matching file was found
 * @docLink packages/core/workspace-config#load-sk-workspace-config
 */
export function loadSkWorkspaceConfig(dir: string, name?: string): SkWorkspaceConfigFile | null {
  const resolvedDir = resolve(dir);
  const canonicalName = name && name !== "default" ? name : undefined;

  const filename = workspaceConfigFilename(canonicalName);
  const filePath = join(resolvedDir, filename);
  if (existsSync(filePath)) {
    return parseConfigFile(filePath, canonicalName ?? "default");
  }

  if (!canonicalName) {
    const found = listSkWorkspaceConfigs(resolvedDir);
    if (found.length === 1) return found[0]!;
  }

  return null;
}

/**
 * Serialize and write a workspace config to `dir`, creating the directory if needed.
 *
 * @param dir - Target directory (created recursively if absent)
 * @param config - Config object to serialize as YAML
 * @param name - Optional workspace name (omit for the default `skaile.yaml`)
 * @returns Absolute path of the written file
 * @docLink packages/core/workspace-config#save-sk-workspace-config
 */
export function saveSkWorkspaceConfig(
  dir: string,
  config: SkWorkspaceConfig,
  name?: string,
): string {
  const resolvedDir = resolve(dir);
  const filename = workspaceConfigFilename(name);
  const filePath = join(resolvedDir, filename);

  if (!existsSync(resolvedDir)) {
    mkdirSync(resolvedDir, { recursive: true });
  }

  writeFileSync(filePath, encodeSkaileYaml(config));
  return filePath;
}

/**
 * List all workspace config files present in `dir`, sorted alphabetically by name.
 * Returns an empty array when `dir` does not exist or is unreadable.
 *
 * @param dir - Directory to scan for `skaile.yaml` and `*.skaile.yaml` files
 * @returns Array of parsed config file descriptors
 * @docLink packages/core/workspace-config#list-sk-workspace-configs
 */
export function listSkWorkspaceConfigs(dir: string): SkWorkspaceConfigFile[] {
  const resolvedDir = resolve(dir);
  if (!existsSync(resolvedDir)) return [];

  const configs: SkWorkspaceConfigFile[] = [];
  try {
    for (const entry of readdirSync(resolvedDir)) {
      if (isWorkspaceConfigFilename(entry)) {
        const wsName = workspaceNameFromFilename(entry);
        const parsed = parseConfigFile(join(resolvedDir, entry), wsName);
        if (parsed) configs.push(parsed);
      }
    }
  } catch {
    /* unreadable dir */
  }

  return configs.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Resolve the effective workspace config by stacking configs across three scope levels:
 * user (`~/.skaile/`) < app (`opts.appDir`) < project (`projectDir`).
 *
 * Each level's config is merged with `mergeSkWorkspaceConfigs`, with higher-priority
 * scopes winning for scalars and unions applied for arrays (deny lists, directories, etc.).
 * Returns an empty object `{}` when no config files are found.
 *
 * @param projectDir - Root directory of the current project
 * @param opts - Optional overrides: `name` selects a named workspace (default: `"default"`),
 *               `appDir` inserts an app-level config between user and project scopes
 * @returns Merged effective `SkWorkspaceConfig`
 * @docLink packages/core/workspace-config#resolve-sk-workspace-config
 */
export function resolveSkWorkspaceConfig(
  projectDir: string,
  opts?: { name?: string; appDir?: string },
): SkWorkspaceConfig {
  const name = opts?.name ?? SK_WORKSPACE_DEFAULT_NAME;
  const configs: SkWorkspaceConfig[] = [];

  // Surface the legacy-key hard error at the runtime boundary. decodeSkaileYaml
  // stays total (records a `legacy_key_rejected` diagnostic) so editor/preview
  // callers don't crash; the merged-runtime resolver re-throws it here so
  // install / serve fail loudly with the migration hint.
  const assertNoLegacyKey = (file: SkWorkspaceConfigFile | null) => {
    const d = file?.diagnostics?.find((x) => x.code === "legacy_key_rejected");
    if (d) throw new Error(d.message);
  };

  const userConfig = loadSkWorkspaceConfig(join(homedir(), ".skaile"), name);
  assertNoLegacyKey(userConfig);
  if (userConfig) configs.push(userConfig.config);

  if (opts?.appDir) {
    const appConfig = loadSkWorkspaceConfig(opts.appDir, name);
    assertNoLegacyKey(appConfig);
    if (appConfig) configs.push(appConfig.config);
  }

  const projectConfig = loadSkWorkspaceConfig(projectDir, name);
  assertNoLegacyKey(projectConfig);
  if (projectConfig) configs.push(projectConfig.config);

  if (configs.length > 0) return configs.reduce(mergeSkWorkspaceConfigs);

  return {};
}

/**
 * Resolve **only** the user-scope global SSOT (`~/.skaile/skaile.yaml`), without
 * stacking any app/project config. This is the single source of truth for global
 * cross-backend installs (`skaile install --global`). Returns `{}` when the file
 * is absent (edge: no `~/.skaile/skaile.yaml` yet). Re-throws the legacy-key
 * error with the `migrate-skaile-manifest` hint, matching
 * {@link resolveSkWorkspaceConfig}.
 *
 * @returns The normalized user-scope config, or `{}` when absent.
 * @throws {Error} on legacy `repositories:` / `ai_resources:` keys.
 * @docLink packages/core/workspace-config#resolve-global-sk-workspace-config
 */
export function resolveGlobalSkWorkspaceConfig(): SkWorkspaceConfig {
  const file = loadSkWorkspaceConfig(globalSkaileDir(), SK_WORKSPACE_DEFAULT_NAME);
  const legacy = file?.diagnostics?.find((x) => x.code === "legacy_key_rejected");
  if (legacy) throw new Error(legacy.message);
  return file?.config ?? {};
}

/**
 * Resolve the set of coding-agent backends a global install fans out to:
 * the explicit `global_backends:` list from `~/.skaile/skaile.yaml` when set,
 * otherwise every {@link DriverTarget} whose global backend root dir already
 * exists under `~` (auto-detection). May return an empty array when no backend
 * is declared and none is present on disk.
 *
 * @returns Deduplicated, declaration-order list of global backends.
 * @throws {Error} on legacy keys in `~/.skaile/skaile.yaml`.
 * @docLink packages/core/workspace-config#resolve-global-backends
 */
export function resolveGlobalBackends(): DriverTarget[] {
  const explicit = resolveGlobalSkWorkspaceConfig().global_backends;
  if (explicit && explicit.length > 0) return [...new Set(explicit)];
  return SUPPORTED_DRIVER_TARGETS.filter((t) => existsSync(globalBackendRoot(t)));
}

/**
 * Walk upward from `startDir` looking for a `skaile.yaml` (or `*.skaile.yaml`).
 * Returns the directory containing the first match, or `undefined` if none found.
 *
 * Stops after 20 levels to avoid scanning all the way to `/`.
 *
 * @param startDir - Directory to start searching from (usually `process.cwd()`)
 * @returns Absolute path to the workspace root, or `undefined`
 * @docLink packages/core/workspace-config#find-workspace-root
 */
export function findWorkspaceRoot(startDir: string): string | undefined {
  let dir = resolve(startDir);
  const root = parsePath(dir).root; // filesystem root (e.g. "/" or "C:\\")
  let depth = 0;

  while (depth < 20) {
    if (existsSync(join(dir, SKAILE_YAML_DEFAULT))) return dir;
    // Check for named workspace configs (*.skaile.yaml)
    try {
      for (const entry of readdirSync(dir)) {
        if (entry.endsWith(SKAILE_YAML_SUFFIX)) return dir;
      }
    } catch {
      /* unreadable */
    }

    if (dir === root) break;
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
    depth++;
  }
  return undefined;
}

// ── Merge logic ───────────────────────────────────────────────────────────────

/**
 * Deep-merge two workspace configs according to stacking rules.
 * `overlay` takes priority over `base` for scalar fields.
 * Arrays use concatenation + deduplication; deny lists always union.
 * Mounts and connectors are merged by `id` (overlay entry wins for same id).
 * Hooks are concatenated in order (base first, overlay appended).
 *
 * @param base - Lower-priority config (e.g. user or app scope)
 * @param overlay - Higher-priority config (e.g. project scope)
 * @returns Merged `SkWorkspaceConfig`
 * @docLink packages/core/workspace-config#merge-sk-workspace-configs
 */
export function mergeSkWorkspaceConfigs(
  base: SkWorkspaceConfig,
  overlay: SkWorkspaceConfig,
): SkWorkspaceConfig {
  return {
    name: overlay.name ?? base.name,
    description: overlay.description ?? base.description,

    // agent_config: per-profile key, overlay profile wins
    agent_config: {
      ...(base.agent_config ?? {}),
      ...(overlay.agent_config ?? {}),
    },

    // startup: overlay wins (project-level only)
    startup: overlay.startup ?? base.startup,

    // mounts: by id — overlay wins
    mounts: mergeById(base.mounts ?? [], overlay.mounts ?? []),

    // connectors: by id — overlay wins
    connectors: mergeById(base.connectors ?? [], overlay.connectors ?? []),

    // mcp_servers: by id — overlay wins
    mcp_servers: mergeById(base.mcp_servers ?? [], overlay.mcp_servers ?? []),

    agent: mergeAgentConfig(base.agent, overlay.agent),

    workspace: mergeWorkspaceConfig(base.workspace, overlay.workspace),

    // secrets: overlay wins entirely
    secrets: overlay.secrets ?? base.secrets,

    // telemetry: overlay wins entirely (raw pass-through)
    telemetry: overlay.telemetry ?? base.telemetry,

    // dependencies: concatenate and dedupe
    dependencies: dedupe([...(base.dependencies ?? []), ...(overlay.dependencies ?? [])]),

    // global_backends: user-scope SSOT key — overlay wins entirely
    global_backends: overlay.global_backends ?? base.global_backends,

    // sources: dedupe by url — overlay entry wins for the same url
    sources: dedupeByKey([...(base.sources ?? []), ...(overlay.sources ?? [])], "url"),

    // stores: dedupe by url — overlay wins
    stores: dedupeByKey([...(base.stores ?? []), ...(overlay.stores ?? [])], "url"),

    // overrides: dedupe by ref — overlay wins
    overrides: dedupeByKey([...(base.overrides ?? []), ...(overlay.overrides ?? [])], "ref"),

    // plugins: concatenate + dedupe (like dependencies)
    plugins: dedupe([...(base.plugins ?? []), ...(overlay.plugins ?? [])]),

    // deploy: overlay wins entirely (scalar-like selection)
    deploy: overlay.deploy ?? base.deploy,
  };
}

/** Merge the `agent:` block — scalars overlay-win, deny lists union, hooks concat. */
function mergeAgentConfig(
  base: AgentConfig | undefined,
  overlay: AgentConfig | undefined,
): AgentConfig {
  return {
    definition: overlay?.definition ?? base?.definition,
    context: overlay?.context ?? base?.context,
    permissions: {
      ...base?.permissions,
      ...stripUndefined(overlay?.permissions),
      deny_write: dedupe([
        ...(base?.permissions?.deny_write ?? []),
        ...(overlay?.permissions?.deny_write ?? []),
      ]),
      deny_read: dedupe([
        ...(base?.permissions?.deny_read ?? []),
        ...(overlay?.permissions?.deny_read ?? []),
      ]),
    },
    subagents: {
      ...(base?.subagents ?? {}),
      ...(overlay?.subagents ?? {}),
    },
    hooks: {
      pre_flow: [...(base?.hooks?.pre_flow ?? []), ...(overlay?.hooks?.pre_flow ?? [])],
      post_flow: [...(base?.hooks?.post_flow ?? []), ...(overlay?.hooks?.post_flow ?? [])],
      pre_node: [...(base?.hooks?.pre_node ?? []), ...(overlay?.hooks?.pre_node ?? [])],
      post_node: [...(base?.hooks?.post_node ?? []), ...(overlay?.hooks?.post_node ?? [])],
    },
  };
}

/** Merge the `workspace:` block — directories/ignore union, setup/container overlay-win. */
function mergeWorkspaceConfig(
  base: WorkspaceLayoutConfig | undefined,
  overlay: WorkspaceLayoutConfig | undefined,
): WorkspaceLayoutConfig {
  return {
    directories: dedupe([...(base?.directories ?? []), ...(overlay?.directories ?? [])]),
    git: {
      init: overlay?.git?.init ?? base?.git?.init,
      ignore: dedupe([...(base?.git?.ignore ?? []), ...(overlay?.git?.ignore ?? [])]),
    },
    setup: overlay?.setup ?? base?.setup,
    container: overlay?.container ?? base?.container,
  };
}

// ── Diagnostics ───────────────────────────────────────────────────────────────

/** Severity of a {@link Diagnostic} produced by {@link decodeSkaileYaml}. */
export type DiagnosticSeverity = "error" | "warning" | "info";

/**
 * A structured note about a decoded `skaile.yaml` — a syntax error, a tolerated
 * legacy/non-canonical shape that was normalized, or an unrecognized enum value.
 * {@link decodeSkaileYaml} never throws; it reports problems here so each caller
 * (platform editor, runner, CLI) decides how loud to be.
 */
export interface Diagnostic {
  /** Stable machine code, e.g. `"legacy_key_camelcase"` / `"unknown_driver"`. */
  code: string;
  severity: DiagnosticSeverity;
  /** Human-readable, actionable message. */
  message: string;
  /** Dotted path into the config the note refers to, when applicable. */
  path?: string;
}

/** Result of {@link decodeSkaileYaml}: the normalized config plus any notes. */
export interface DecodeResult {
  config: SkWorkspaceConfig;
  diagnostics: Diagnostic[];
}

/**
 * Known runtime drivers — mirrors the keys of `DRIVER_DEFAULTS` (framework.ts).
 * Kept local to avoid a settings/framework ↔ workspace-config import cycle; a
 * drift test asserts parity.
 */
const KNOWN_DRIVERS = ["omp", "claude-sdk", "codex"] as const;

/**
 * Known LLM/voice providers — mirrors `ALL_PROVIDERS` (settings.ts). Local for
 * the same cycle reason; a drift test asserts parity.
 */
const KNOWN_PROVIDERS = [
  "anthropic",
  "openai",
  "google",
  "mistral",
  "groq",
  "openrouter",
  "deepseek",
  "xai",
  "together",
  "fireworks",
  "deepgram",
  "elevenlabs",
] as const;

/**
 * Known AI cloud transports — mirrors `SUPPORTED_CLOUDS` (bridge/src/cloud.ts,
 * the canonical list). Local copy to avoid a core ↔ bridge import cycle; a
 * drift test asserts parity.
 */
const KNOWN_CLOUDS = ["default", "bedrock", "vertex", "azure", "gateway"] as const;

const KNOWN_ACCESS = ["read-only", "read-write"] as const;

/** Valid launcher strategies for a local (stdio) MCP server. */
const KNOWN_LAUNCHERS = ["recipe", "package-runner", "path"] as const;
// Runtime transport literals the runner's external-mcp switch branches on.
// "streamable-http" is an MCP-spec alias normalized to "http" before this check.
const KNOWN_TRANSPORTS = ["stdio", "sse", "http"] as const;

// ── Raw → canonical normalization ─────────────────────────────────────────────

/** Scalar fields that identify a flat (un-profiled) agent-config block. */
const AGENT_PROFILE_FIELDS = [
  "driver",
  "provider",
  "cloud",
  "model",
  "thinking",
  "effort",
  "skills_dir",
  "agents_dir",
  "prompts_dir",
] as const;

/**
 * Coerce a raw agent-config block into the canonical profile-map shape
 * (`{ default: {...}, … }`).
 *
 * Accepts two authoring shapes:
 *   - **Profile map** (canonical): `{ default: { driver, model }, staging: {…} }`
 *   - **Flat profile**: `{ driver, model, provider }` — emitted by clients that
 *     serialize a runtime config object straight to YAML. Wrapped as the
 *     `default` profile so `settingsFromWorkspaceConfig` finds it.
 *
 * `wrappedFlat` reports whether a flat block was wrapped, so the caller can emit
 * a diagnostic. A block is flat when any known profile field is present as a
 * scalar; a profile named e.g. `driver` carries an object value, so the scalar
 * check disambiguates.
 */
function normalizeAgentConfig(raw: Record<string, unknown>): {
  profiles: Record<string, AgentConfigProfile>;
  wrappedFlat: boolean;
} {
  const looksFlat = AGENT_PROFILE_FIELDS.some((f) => typeof raw[f] === "string");
  return looksFlat
    ? { profiles: { default: raw as AgentConfigProfile }, wrappedFlat: true }
    : { profiles: raw as Record<string, AgentConfigProfile>, wrappedFlat: false };
}

/**
 * Normalize a raw parsed YAML object into the canonical SkWorkspaceConfig shape.
 * Pure structural pass; see {@link decodeSkaileYaml} for the diagnostics-aware
 * entry point. Tolerates backward-compatible fields:
 *   - `agent-config` (hyphen) / `agent_config` (underscore) / `agentConfig` (camelCase) → `agent_config`
 *   - flat agent-config (`{ driver, model }`) → `{ default: { driver, model } }`
 *
 * Hard cut: legacy top-level keys `repositories:` and `ai_resources:` (any case)
 * now throw — the migration is performed by the `migrate-skaile-manifest` skill.
 * @docLink packages/core/workspace-config#normalize-config
 */
export function normalizeConfig(raw: Record<string, unknown>): SkWorkspaceConfig {
  return normalizeConfigInternal(raw).config;
}

/**
 * Normalize the `agent-config` block (hyphen/underscore/camelCase key + flat
 * single-profile shape) onto `config.agent_config`, recording warnings for
 * non-canonical shapes. No-op when no recognized key is present.
 */
function normalizeAgentConfigSection(
  raw: Record<string, unknown>,
  config: SkWorkspaceConfig,
  diagnostics: Diagnostic[],
): void {
  // agent_config: accept hyphen (canonical), underscore, or camelCase, plus a
  // flat single-profile block. Non-canonical shapes are normalized + flagged.
  let agentConfigRaw: unknown;
  let agentConfigKey: string | undefined;
  if (raw["agent-config"] !== undefined) {
    agentConfigRaw = raw["agent-config"];
    agentConfigKey = "agent-config";
  } else if (raw.agent_config !== undefined) {
    agentConfigRaw = raw.agent_config;
    agentConfigKey = "agent_config";
  } else if (raw.agentConfig !== undefined) {
    agentConfigRaw = raw.agentConfig;
    agentConfigKey = "agentConfig";
  }
  if (!agentConfigRaw || typeof agentConfigRaw !== "object" || Array.isArray(agentConfigRaw)) {
    return;
  }
  if (agentConfigKey === "agentConfig") {
    diagnostics.push({
      code: "legacy_key_camelcase",
      severity: "warning",
      message: 'Non-canonical key "agentConfig" — use "agent-config".',
      path: "agentConfig",
    });
  }
  const { profiles, wrappedFlat } = normalizeAgentConfig(agentConfigRaw as Record<string, unknown>);
  if (wrappedFlat) {
    diagnostics.push({
      code: "legacy_agent_config_flat",
      severity: "warning",
      message:
        'Flat agent-config block wrapped as the "default" profile — nest fields under "default:".',
      path: agentConfigKey,
    });
  }
  config.agent_config = profiles;
}

// Absolute on purpose: `docs/` is not in the published `files` list and the
// skill lives in `skaile-ai/ai-assets`, so bare names resolve to nothing for an
// npm consumer. Keep the literal substrings — tests and the docs grep for them.
const MANIFEST_SCHEMA_DOC_URL =
  "https://github.com/skaile-ai/workspaces/blob/main/docs/concepts/manifest-schema.md";
const MIGRATE_SKILL_HINT =
  "ask your AI assistant to apply the `migrate-skaile-manifest` skill " +
  "(published from https://github.com/skaile-ai/ai-assets)";

/**
 * Hard-cut guard for legacy/migrated top-level keys. `repositories:` /
 * `ai_resources:` were removed with the canonical-identity schema; `publisher:` /
 * `version:` / `assets:` are publication keys that moved to `skaile.manifest.yaml`
 * (2026-06-03 split). All throw with the spec wording — the migration is done by
 * the `migrate-skaile-manifest` skill, not code. decodeSkaileYaml catches the
 * throw and records a single `legacy_key_rejected` diagnostic (the message
 * carries the specifics); resolveSkWorkspaceConfig re-throws it at the runtime
 * boundary.
 *
 * Consumer-facing migration guidance lives in `docs/migration-v2-to-v3.md` and
 * the root `MIGRATION.md`; both quote these strings verbatim so they are
 * greppable from a failing session.
 */
function assertNoLegacyTopLevelKeys(raw: Record<string, unknown>): void {
  if (raw.repositories !== undefined) {
    throw new Error(
      "skaile.yaml: unknown top-level key `repositories:`. The schema changed — " +
        "replace it with `sources:`, a list of `{url, pin?}`. See " +
        `${MANIFEST_SCHEMA_DOC_URL}, or ${MIGRATE_SKILL_HINT}.`,
    );
  }
  if (raw.ai_resources !== undefined || raw.aiResources !== undefined) {
    throw new Error(
      "skaile.yaml: unknown top-level key `ai_resources:`. The schema changed — " +
        "replace it with `dependencies:` (canonical refs) plus the `sources:` / " +
        "`stores:` that resolve them. See " +
        `${MANIFEST_SCHEMA_DOC_URL}, or ${MIGRATE_SKILL_HINT}.`,
    );
  }
  for (const key of ["publisher", "version", "assets"] as const) {
    if (raw[key] !== undefined) {
      throw new Error(
        `skaile.yaml: \`${key}:\` is no longer valid here — publication keys ` +
          "(publisher, version, assets) describe what a repo publishes and now live in " +
          `skaile.manifest.yaml. See ${MANIFEST_SCHEMA_DOC_URL}, or ${MIGRATE_SKILL_HINT}.`,
      );
    }
  }
}

/** Normalize a single raw `overrides[]` item into an OverrideEntry, or `null` to skip. */
function normalizeOverrideEntry(item: unknown): OverrideEntry | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const o = item as Record<string, unknown>;
  const ref = typeof o.ref === "string" ? o.ref : "";
  const source = typeof o.source === "string" ? o.source : "";
  if (!ref || !source) return null;
  if (typeof o.reason !== "string") {
    throw new Error(
      `skaile.yaml: overrides[] entry for ${ref}: reason is required ` +
        "and must be a non-empty string.",
    );
  }
  if (o.reason.trim().length === 0) {
    throw new Error(`skaile.yaml: overrides[] entry for ${ref}: reason must not be empty.`);
  }
  return { ref, source, reason: o.reason };
}

/** Normalize the consumption-half `stores[]` array onto `config`. */
function normalizeStoresSection(raw: Record<string, unknown>, config: SkWorkspaceConfig): void {
  if (!Array.isArray(raw.stores)) return;
  const entries: StoreEntry[] = [];
  for (const item of raw.stores) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const s = item as Record<string, unknown>;
    const url = typeof s.url === "string" ? s.url : "";
    if (!url) continue;
    entries.push({ url });
  }
  if (entries.length > 0) config.stores = entries;
}

/** Normalize the consumption-half `overrides[]` array onto `config`. */
function normalizeOverridesSection(raw: Record<string, unknown>, config: SkWorkspaceConfig): void {
  if (!Array.isArray(raw.overrides)) return;
  const entries: OverrideEntry[] = [];
  for (const item of raw.overrides) {
    const entry = normalizeOverrideEntry(item);
    if (entry) entries.push(entry);
  }
  if (entries.length > 0) config.overrides = entries;
}

/** Normalize the consumption-half `stores[]` and `overrides[]` arrays. */
function normalizeConsumptionSection(
  raw: Record<string, unknown>,
  config: SkWorkspaceConfig,
): void {
  normalizeStoresSection(raw, config);
  normalizeOverridesSection(raw, config);
}

/**
 * Normalize the tolerantly-cast sections (mounts, connectors, mcp_servers,
 * plugins, deploy, agent, workspace, secrets, compaction, patches). Structural
 * validation is deferred — the runtime tolerates unexpected shapes.
 */
function normalizeStructuralSections(
  raw: Record<string, unknown>,
  config: SkWorkspaceConfig,
  diagnostics: Diagnostic[],
): void {
  if (Array.isArray(raw.mounts)) {
    config.mounts = raw.mounts as MountDeclaration[];
  }
  // connectors: no data_resources fallback — clean break
  if (Array.isArray(raw.connectors)) {
    config.connectors = raw.connectors as ConnectorDeclaration[];
  }
  if (Array.isArray(raw.mcp_servers)) {
    // Strip unknown launcher values at parse time so they never reach the runner.
    // A warning diagnostic is emitted for each stripped entry (mirroring unknown_driver/access).
    config.mcp_servers = (raw.mcp_servers as McpServerDeclaration[]).map((entry) => {
      if (
        entry.launcher !== undefined &&
        !(KNOWN_LAUNCHERS as readonly string[]).includes(entry.launcher)
      ) {
        diagnostics.push({
          code: "unknown_launcher",
          severity: "warning",
          message: `Unknown launcher "${entry.launcher}" on mcp_servers entry "${entry.id}" — expected one of: ${KNOWN_LAUNCHERS.join(", ")}.`,
          path: `mcp_servers.${entry.id}.launcher`,
        });
        const { launcher: _dropped, ...rest } = entry;
        return rest as McpServerDeclaration;
      }
      return entry;
    });
  }
  if (Array.isArray(raw.plugins)) {
    const specs = raw.plugins.filter((p): p is string => typeof p === "string" && p.length > 0);
    if (specs.length > 0) config.plugins = specs;
  }
  // deploy: structural only — the target id is resolved/validated against
  // pluginRegistry at deploy time, not here. A block missing a string target is
  // dropped silently (mirrors connectors' tolerant cast).
  if (raw.deploy && typeof raw.deploy === "object" && !Array.isArray(raw.deploy)) {
    const d = raw.deploy as Record<string, unknown>;
    if (typeof d.target === "string" && d.target.length > 0) {
      config.deploy = { target: d.target };
      if (d.config !== undefined) config.deploy.config = d.config;
    }
  }
  if (raw.agent) config.agent = raw.agent as AgentConfig;
  if (raw.workspace) config.workspace = raw.workspace as WorkspaceLayoutConfig;
  if (raw.secrets && typeof raw.secrets === "object") {
    config.secrets = raw.secrets as SecretsConfig;
  }
  if (raw.compaction && typeof raw.compaction === "object" && !Array.isArray(raw.compaction)) {
    config.compaction = raw.compaction as CompactionConfig;
  }
  if (raw.patches && typeof raw.patches === "object" && !Array.isArray(raw.patches)) {
    config.patches = raw.patches as Record<string, string>;
  }
}

/** Normalize `dependencies[]`, `sources[]`, and the raw `telemetry` pass-through. */
function normalizeDependencySection(raw: Record<string, unknown>, config: SkWorkspaceConfig): void {
  if (Array.isArray(raw.dependencies)) {
    config.dependencies = raw.dependencies.filter((d): d is string => typeof d === "string");
  }

  // global_backends: accept hyphen (canonical) or underscore. Keep only known
  // DriverTargets — an unknown id is dropped here; `resolveGlobalBackends`
  // additionally falls back to auto-detection when the result is empty.
  const rawBackends = raw["global-backends"] ?? raw.global_backends;
  if (Array.isArray(rawBackends)) {
    const valid = rawBackends.filter((b): b is DriverTarget =>
      (SUPPORTED_DRIVER_TARGETS as string[]).includes(b as string),
    );
    if (valid.length > 0) config.global_backends = [...new Set(valid)];
  }

  if (Array.isArray(raw.sources)) {
    const entries: SourceEntry[] = [];
    for (const item of raw.sources) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue;
      const s = item as Record<string, unknown>;
      const url = typeof s.url === "string" ? s.url : "";
      if (!url) continue;
      const entry: SourceEntry = { url };
      if (typeof s.pin === "string" && s.pin.length > 0) entry.pin = s.pin;
      entries.push(entry);
    }
    if (entries.length > 0) config.sources = entries;
  }

  // telemetry: pass through raw — parsed by @skaile/workspaces/telemetry
  if (raw.telemetry && typeof raw.telemetry === "object" && !Array.isArray(raw.telemetry)) {
    config.telemetry = raw.telemetry as Record<string, unknown>;
  }
}

/** Diagnostics-collecting core of {@link normalizeConfig}. */
function normalizeConfigInternal(raw: Record<string, unknown>): DecodeResult {
  const config: SkWorkspaceConfig = {};
  const diagnostics: Diagnostic[] = [];

  if (raw.name) config.name = String(raw.name);
  if (raw.description) config.description = String(raw.description);

  normalizeAgentConfigSection(raw, config, diagnostics);

  if (Array.isArray(raw.startup)) {
    config.startup = raw.startup as StartupDirective[];
  }

  assertNoLegacyTopLevelKeys(raw);
  normalizeConsumptionSection(raw, config);
  normalizeStructuralSections(raw, config, diagnostics);
  normalizeDependencySection(raw, config);

  return { config, diagnostics };
}

// ── Codec (decode / encode) ────────────────────────────────────────────────────

/**
 * Decode `skaile.yaml` text into a normalized {@link SkWorkspaceConfig} plus
 * {@link Diagnostic}s. Total — never throws: a YAML syntax error or a non-object
 * root is reported as an `error`-severity diagnostic with an empty config.
 * Legacy/non-canonical shapes (camelCase keys, flat agent-config, the old
 * ai_resources object) are normalized and reported as `warning`s; unrecognized
 * driver/provider/access values are reported as `warning`s too.
 *
 * Pair with {@link encodeSkaileYaml} for round-tripping. The round-trip is
 * canonical-lossless on the typed model (`decode(encode(config)).config` deep-
 * equals `config`) but does not preserve comments or key order — use
 * `WorkspaceYamlEditor` for comment-preserving in-place edits.
 * @docLink packages/core/workspace-config#decode-skaile-yaml
 */
export function decodeSkaileYaml(text: string): DecodeResult {
  let parsed: unknown;
  try {
    parsed = parse(text) ?? {};
  } catch (err) {
    return {
      config: {},
      diagnostics: [
        {
          code: "yaml_syntax",
          severity: "error",
          message: `Invalid YAML: ${err instanceof Error ? err.message : String(err)}`,
        },
      ],
    };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      config: {},
      diagnostics: [
        {
          code: "non_object_root",
          severity: "error",
          message: "skaile.yaml must be a mapping at the top level.",
        },
      ],
    };
  }
  // normalizeConfigInternal hard-throws on legacy keys (repositories:/ai_resources:).
  // decodeSkaileYaml is total by contract, so convert that throw into an
  // `error`-severity diagnostic for editor/preview callers.
  let result: DecodeResult;
  try {
    result = normalizeConfigInternal(parsed as Record<string, unknown>);
  } catch (err) {
    return {
      config: {},
      diagnostics: [
        {
          code: "legacy_key_rejected",
          severity: "error",
          message: err instanceof Error ? err.message : String(err),
        },
      ],
    };
  }
  validateConfigValues(result.config, result.diagnostics);
  return result;
}

/**
 * Encode a {@link SkWorkspaceConfig} to canonical `skaile.yaml` text:
 * deterministic key order, the hyphenated `agent-config` key, only defined
 * fields. This is the single sanctioned writer — clients should build a typed
 * config and encode it here rather than hand-serializing YAML.
 * @docLink packages/core/workspace-config#encode-skaile-yaml
 */
export function encodeSkaileYaml(config: SkWorkspaceConfig): string {
  return stringify(toCanonicalObject(config), { lineWidth: 120 });
}

/** Append `warning` diagnostics for unrecognized driver/provider/access values. */
function validateConfigValues(config: SkWorkspaceConfig, diagnostics: Diagnostic[]): void {
  for (const [profile, p] of Object.entries(config.agent_config ?? {})) {
    if (p?.driver && !(KNOWN_DRIVERS as readonly string[]).includes(p.driver)) {
      diagnostics.push({
        code: "unknown_driver",
        severity: "warning",
        message: `Unknown driver "${p.driver}" — expected one of: ${KNOWN_DRIVERS.join(", ")}.`,
        path: `agent-config.${profile}.driver`,
      });
    }
    if (p?.provider && !(KNOWN_PROVIDERS as readonly string[]).includes(p.provider)) {
      diagnostics.push({
        code: "unknown_provider",
        severity: "warning",
        message: `Unknown provider "${p.provider}" — expected one of: ${KNOWN_PROVIDERS.join(", ")}.`,
        path: `agent-config.${profile}.provider`,
      });
    }
    if (p?.cloud && !(KNOWN_CLOUDS as readonly string[]).includes(p.cloud)) {
      diagnostics.push({
        code: "unknown_cloud",
        severity: "warning",
        message: `Unknown cloud "${p.cloud}" — expected one of: ${KNOWN_CLOUDS.join(", ")}.`,
        path: `agent-config.${profile}.cloud`,
      });
    }
    if (p?.cloud === "gateway" && !p.cloud_config?.base_url) {
      diagnostics.push({
        code: "gateway_missing_base_url",
        severity: "warning",
        message: 'cloud "gateway" requires cloud_config.base_url — the session has no endpoint.',
        path: `agent-config.${profile}.cloud_config.base_url`,
      });
    }
  }
  const checkAccess = (access: string | undefined, path: string) => {
    if (access && !(KNOWN_ACCESS as readonly string[]).includes(access)) {
      diagnostics.push({
        code: "unknown_access",
        severity: "warning",
        message: `Unknown access "${access}" — expected "read-only" or "read-write".`,
        path,
      });
    }
  };
  for (const c of config.connectors ?? []) checkAccess(c.access, `connectors.${c.id}.access`);
  for (const m of config.mounts ?? []) checkAccess(m.access, `mounts.${m.id}.access`);

  const network = config.agent?.permissions?.network;
  if (network) {
    if (network.mode && !(KNOWN_NETWORK_MODES as readonly string[]).includes(network.mode)) {
      diagnostics.push({
        code: "unknown_network_mode",
        severity: "warning",
        message: `Unknown network mode "${network.mode}" — expected one of: ${KNOWN_NETWORK_MODES.join(", ")}.`,
        path: "agent.permissions.network.mode",
      });
    }
    if (network.allowlist && network.allowlist.length > 0 && network.mode !== "allowlist") {
      diagnostics.push({
        code: "unexpected_network_allowlist",
        severity: "warning",
        message: `network.allowlist is set but mode is "${network.mode}" — the allowlist is ignored unless mode is "allowlist".`,
        path: "agent.permissions.network.allowlist",
      });
    }
  }
}

/**
 * Canonical key order for encode. `agent_config` is emitted under the hyphenated
 * `agent-config` key (see {@link CONFIG_KEY_TO_YAML}); any field not listed here
 * is appended afterward so forward-compat keys survive a round-trip.
 */
const CANONICAL_KEY_ORDER: (keyof SkWorkspaceConfig)[] = [
  "name",
  "description",
  "agent_config",
  "dependencies",
  "global_backends",
  "plugins",
  "sources",
  "stores",
  "overrides",
  "startup",
  "connectors",
  "mcp_servers",
  "deploy",
  "mounts",
  "agent",
  "workspace",
  "secrets",
  "telemetry",
  "compaction",
  "patches",
];

/** Internal field name → canonical YAML key, where they differ. */
const CONFIG_KEY_TO_YAML: Partial<Record<keyof SkWorkspaceConfig, string>> = {
  agent_config: "agent-config",
};

function toCanonicalObject(config: SkWorkspaceConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const emit = (key: keyof SkWorkspaceConfig) => {
    const value = config[key];
    if (value === undefined) return;
    out[CONFIG_KEY_TO_YAML[key] ?? key] = value;
  };
  for (const key of CANONICAL_KEY_ORDER) emit(key);
  // Preserve any field not in the canonical order (forward-compat).
  for (const key of Object.keys(config) as (keyof SkWorkspaceConfig)[]) {
    if (CANONICAL_KEY_ORDER.includes(key)) continue;
    emit(key);
  }
  return out;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseConfigFile(filePath: string, name: string): SkWorkspaceConfigFile | null {
  let raw: string;
  try {
    raw = readFileSync(filePath, "utf-8");
  } catch {
    return null;
  }
  const { config, diagnostics } = decodeSkaileYaml(raw);
  // Preserve the legacy contract: an unparseable file reads as "no config".
  if (diagnostics.some((d) => d.code === "yaml_syntax")) return null;
  return { path: filePath, name, config, diagnostics };
}

function dedupe(arr: string[]): string[] {
  return [...new Set(arr)];
}

function dedupeByKey<T extends object>(arr: T[], key: string | ((item: T) => string)): T[] {
  const keyOf = typeof key === "function" ? key : (item: T) => String((item as any)[key] ?? "");
  const seen = new Set<string>();
  const result: T[] = [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const val = keyOf(arr[i]!);
    if (!seen.has(val)) {
      seen.add(val);
      result.unshift(arr[i]!);
    }
  }
  return result;
}

function mergeById<T extends { id: string }>(base: T[], overlay: T[]): T[] {
  const map = new Map<string, T>();
  for (const r of base) map.set(r.id, r);
  for (const r of overlay) map.set(r.id, r);
  return [...map.values()];
}

function stripUndefined<T extends object>(obj?: T): Partial<T> {
  if (!obj) return {};
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) result[k] = v;
  }
  return result as Partial<T>;
}

// ── MCP server loader ─────────────────────────────────────────────────────────

/**
 * Build a `McpServerDeclaration` from a resolved `CatalogEntry` with `kind: "mcp-server"`.
 * Maps `metadata` fields (transport, command, args, env, url, headers) to declaration fields.
 * Returns `null` when the entry has no usable metadata (no command and no url).
 */
function mcpDeclFromCatalogEntry(entry: CatalogEntry): McpServerDeclaration | null {
  const m = entry.metadata;
  if (!m) return null;

  const decl: McpServerDeclaration = { id: entry.name };
  if (m.transport) {
    // Normalize the MCP-spec alias to the runtime literal the runner branches on,
    // then allow-list: an unrecognized value is warned-and-skipped rather than cast
    // through to the runner (mirrors the unknown_launcher diagnostic).
    const raw = String(m.transport);
    const t = raw === "streamable-http" ? "http" : raw;
    if ((KNOWN_TRANSPORTS as readonly string[]).includes(t)) {
      decl.transport = t as (typeof KNOWN_TRANSPORTS)[number];
    } else {
      console.warn(
        `[workspace-config] mcpDeclFromCatalogEntry: unknown transport "${raw}" on entry "${entry.name}" — expected one of: ${KNOWN_TRANSPORTS.join(", ")}. Ignoring.`,
      );
    }
  }
  if (m.command) decl.command = String(m.command);
  if (Array.isArray(m.args)) decl.args = m.args.map(String);
  if (m.env && typeof m.env === "object") decl.env = m.env as Record<string, string>;
  if (m.url) decl.url = String(m.url);
  if (m.headers && typeof m.headers === "object") {
    decl.headers = m.headers as Record<string, string>;
  }
  if (entry.description) decl.description = entry.description;
  if (m.recipe && typeof m.recipe === "object" && !Array.isArray(m.recipe)) {
    const recipe = parseCatalogRecipe(m.recipe as Record<string, unknown>, entry.name);
    if (recipe) decl.recipe = recipe;
  }

  // Must have at least a command (stdio) or url (sse/http) to be useful
  if (!decl.command && !decl.url) return null;
  return decl;
}

/**
 * Parse a raw recipe object from `MCP.md` metadata into a validated
 * {@link AssetRecipe}. Returns `null` when there is no effective attr, or when
 * validation fails (the recipe is then treated as absent — the server may still
 * be usable via command/url without recipe substitution). `entryName` is used
 * only in the warning message.
 */
function parseCatalogRecipe(r: Record<string, unknown>, entryName: string): AssetRecipe | null {
  const hasFlake = typeof r.flake === "string";
  // BYO-flake form: `attr` is optional and defaults to "default"; legacy form
  // requires `attr`. Compute the effective attr to validate.
  const rawAttr = typeof r.attr === "string" ? r.attr : undefined;
  const effectiveAttr = rawAttr ?? (hasFlake ? DEFAULT_RECIPE_ATTR : undefined);
  if (effectiveAttr === undefined) return null;

  try {
    validateAssetRecipeAttr(effectiveAttr);
    if (hasFlake) {
      // Validate the flake source too; invalid flake drops the WHOLE recipe
      // (do not silently fall back to a platform-flake attr — a publisher
      // that declared a flake intends to build it; building a same-named
      // platform attr would be a confusing security footgun).
      validateAssetRecipeFlake(r.flake as string);
      const recipe: AssetRecipe = { attr: effectiveAttr, flake: r.flake as string };
      if (typeof r.publisher === "string") recipe.publisher = r.publisher;
      return recipe;
    }
    return { attr: effectiveAttr };
  } catch (err) {
    // Invalid attr or flake — skip the recipe field entirely (treat as if
    // recipe was absent). The MCP server may still be usable via a
    // command/url without recipe substitution.
    console.warn(
      `[workspace-config] mcpDeclFromCatalogEntry: invalid recipe for entry "${entryName}": ${err instanceof Error ? err.message : String(err)}`,
    );
    return null;
  }
}

/**
 * Merge two `McpServerDeclaration` objects field-by-field.
 * `overlay` values win for every non-undefined field; `base` provides defaults.
 */
function mergeMcpDeclarations(
  base: McpServerDeclaration,
  overlay: McpServerDeclaration,
): McpServerDeclaration {
  return {
    id: overlay.id,
    transport: overlay.transport ?? base.transport,
    command: overlay.command ?? base.command,
    args: overlay.args ?? base.args,
    env: overlay.env ? (base.env ? { ...base.env, ...overlay.env } : overlay.env) : base.env,
    url: overlay.url ?? base.url,
    headers: overlay.headers
      ? base.headers
        ? { ...base.headers, ...overlay.headers }
        : overlay.headers
      : base.headers,
    description: overlay.description ?? base.description,
    tags: overlay.tags ?? base.tags,
    recipe: overlay.recipe ?? base.recipe,
    // Sticky: once either side carried folded `.instance.json` secrets, the
    // merged `env` does too — the flag must not be lost by an overlay.
    instanceSecretsFolded: overlay.instanceSecretsFolded ?? base.instanceSecretsFolded,
  };
}

/**
 * Scan `<projectDir>/.skaile/assets/mcp-server/<name>/MCP.md` for MCP servers
 * that the platform materializer has written to disk for a subscribed asset.
 *
 * For each immediate subdirectory containing an `MCP.md`, the manifest is parsed
 * via {@link fromMcpServerMdContent} and mapped to a declaration via
 * {@link mcpDeclFromCatalogEntry}. A sibling `.instance.json` (written by the
 * platform materializer) is folded into `decl.env`.
 *
 * **Env-fold precedence (lowest → highest):**
 *   1. `MCP.md` `env` defaults
 *   2. string-valued entries of `.instance.json` `config` (non-string config
 *      values — objects/arrays/numbers — are skipped so env never contains
 *      `[object Object]`)
 *   3. `.instance.json` `resolvedSecrets` (always folded, all keys)
 *
 * Folded secret *values* are never logged. A malformed `MCP.md` for one asset
 * is caught per-directory and skipped (warned without secret values) so one bad
 * asset never aborts the whole scan.
 *
 * @param projectDir - Workspace root containing `.skaile/assets/mcp-server/`
 * @returns Declarations for every materialized MCP asset (empty if the dir is absent)
 */
// Strip a publisher scope so a declared `@pub/name` matches a materialized dir
// named `name` (and vice versa) without a false strict-mode exclusion.
const unscopedTail = (x: string): string => (x.includes("/") ? x.split("/").pop()! : x);

/**
 * Fold a sibling `.instance.json` (written by the platform materializer) into
 * `decl.env`. Env-fold precedence (lowest → highest): existing `decl.env` <
 * string-valued `config` entries < `resolvedSecrets`. A malformed transient
 * `.instance.json` must not nuke a valid MCP server — parse errors are caught
 * here (warned without secret values) so the decl survives with its `MCP.md`
 * env. No-op when the file is absent. `name` is used only in the warning.
 */
function foldInstanceEnv(decl: McpServerDeclaration, instancePath: string, name: string): void {
  if (!existsSync(instancePath)) return;
  try {
    const instance = JSON.parse(readFileSync(instancePath, "utf8")) as {
      config?: Record<string, unknown>;
      resolvedSecrets?: Record<string, unknown>;
    };

    const env: Record<string, string> = { ...(decl.env ?? {}) };

    // string-config < resolvedSecrets (folded after, so secrets win)
    if (instance.config && typeof instance.config === "object") {
      for (const [k, v] of Object.entries(instance.config)) {
        if (typeof v === "string") env[k] = v;
      }
    }
    if (instance.resolvedSecrets && typeof instance.resolvedSecrets === "object") {
      for (const [k, v] of Object.entries(instance.resolvedSecrets)) {
        // Guard like the config fold above — a non-string secret (serializer
        // bug) must not land in subprocess env as "[object Object]"/"null".
        if (typeof v === "string") {
          env[k] = v;
          // Mark the decl so disk-persisting consumers skip it — these are live
          // plaintext credentials that must never leave memory.
          decl.instanceSecretsFolded = true;
        }
      }
    }

    if (Object.keys(env).length > 0) decl.env = env;
  } catch (err) {
    // Never include secret values in the warning — only the failing path + message.
    console.warn(
      `[workspace-config] foldInstanceEnv: ignoring malformed ".instance.json" for "${name}": ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

/**
 * Derive a single MCP declaration from a materialized on-disk subdir.
 * Returns `null` when the subdir has no `MCP.md` or when parsing fails
 * (warns without aborting the caller's scan).
 */
export function deriveSingleMaterializedMcpDeclaration(
  subDir: string,
  name: string,
): McpServerDeclaration | null {
  const mdPath = join(subDir, "MCP.md");
  if (!existsSync(mdPath)) return null;

  try {
    const mdContent = readFileSync(mdPath, "utf8");
    const { body } = parseFrontmatter(mdContent);
    const entry = fromMcpServerMdContent(mdContent, name, mdPath);
    const decl = mcpDeclFromCatalogEntry(entry);
    if (!decl) return null;

    decl.id = entry.name || name;
    foldInstanceEnv(decl, join(subDir, ".instance.json"), name);
    const trimmedBody = body.trim();
    if (trimmedBody.length > 0) decl.instructions = trimmedBody;
    return decl;
  } catch (err) {
    // Never include secret values — only the failing path + message.
    console.warn(
      `[workspace-config] deriveSingleMaterializedMcpDeclaration: failed to load "${mdPath}": ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
    return null;
  }
}

function loadMaterializedMcpDeclarations(projectDir: string): McpServerDeclaration[] {
  const dir = join(projectDir, ".skaile/assets/mcp-server");
  if (!existsSync(dir)) return [];

  const result: McpServerDeclaration[] = [];

  let subdirs: string[];
  try {
    subdirs = readdirSync(dir, { withFileTypes: true })
      .filter((e) => e.isDirectory())
      .map((e) => e.name);
  } catch {
    return [];
  }

  for (const name of subdirs) {
    const subDir = join(dir, name);
    const decl = deriveSingleMaterializedMcpDeclaration(subDir, name);
    if (decl) result.push(decl);
  }

  return result;
}

/** Immediate subdirectory names of `dir`; `[]` when it is missing or unreadable. */
function subdirNames(dir: string): string[] {
  try {
    return readdirSync(dir, { withFileTypes: true })
      .filter((e) => e.isDirectory())
      .map((e) => e.name);
  } catch {
    return [];
  }
}

/**
 * MCP declarations derived from **store-staged** bytes. `skaile add
 * mcp-server:@pub/name` fetches the asset into
 * `<projectDir>/.skaile/cache/store/<publisher>/mcp-server/<name>/<version>/`
 * but never copies it into a driver target dir (an MCP is wired at session
 * start, not read as a file by the driver) — so unless this scan runs, the
 * staged `MCP.md` is invisible and the install is a silent no-op (#462).
 *
 * The store cache is a *cache*, so every read is best-effort: a missing or
 * unreadable dir at any level yields no declarations rather than throwing.
 * Nothing prunes superseded version dirs, so multiple versions of one asset can
 * sit side by side; they are walked in **numeric-collation** order (`0.9.0` <
 * `0.10.0`, which a plain lexicographic sort gets backwards) so the newest merges
 * on top and an upgrade never keeps wiring the stale `command` / `args` / `env`.
 *
 * Reusing {@link deriveSingleMaterializedMcpDeclaration} also folds a sibling
 * `.instance.json` — a file the platform materializer writes, but which a store
 * publisher could now ship. That grants no capability the asset's own `MCP.md`
 * `env` / `command` does not already have, and folded values are never logged.
 */
function loadStoreStagedMcpDeclarations(projectDir: string): McpServerDeclaration[] {
  const root = join(projectDir, ".skaile/cache/store");
  if (!existsSync(root)) return [];

  const result: McpServerDeclaration[] = [];
  for (const publisher of subdirNames(root)) {
    const kindDir = join(root, publisher, "mcp-server");
    for (const name of subdirNames(kindDir)) {
      const nameDir = join(kindDir, name);
      const versions = subdirNames(nameDir).sort((a, b) =>
        a.localeCompare(b, "en", { numeric: true }),
      );
      for (const version of versions) {
        const decl = deriveSingleMaterializedMcpDeclaration(join(nameDir, version), name);
        if (decl) result.push(decl);
      }
    }
  }

  return result;
}

/**
 * Multiplier applied to the base filesystem deadline for whole-tree copies.
 * A recursive copy legitimately takes far longer than the single `stat` the
 * base deadline is sized for, and a false timeout here would silently drop a
 * declared skill.
 */
const TREE_COPY_DEADLINE_FACTOR = 4;

/**
 * Copy each materialized skill the platform materializer wrote to
 * `<projectDir>/.skaile/assets/skill/<name>/` into the active driver's native
 * skills dir (`<projectDir>/<skillsDir>/<name>/`) so native discovery surfaces
 * them to the LLM. Mirrors {@link loadMaterializedMcpDeclarations}: the scan
 * ALWAYS runs, independent of `dependencies:`.
 *
 * Only immediate subdirectories containing a `SKILL.md` are staged. The copy is
 * idempotent (`force: true` overwrites a previously staged copy on refresh).
 * `.instance.json` is NEVER copied — it can hold a materializer's
 * `resolvedSecrets`, which must not leak into `.claude/skills/`; Claude Code
 * skills do not consume it either.
 *
 * **Async by necessity.** Both `projectDir` and the driver's skills dir can sit
 * on (or beside) an rclone FUSE mount. The former synchronous `existsSync` +
 * `cpSync` pair parked the whole event loop for as long as the mount took to
 * answer — unbounded, and invisible to `try/catch` because a hang is not an
 * error. Every filesystem call here is now awaited and deadline-bounded, so a
 * wedged mount costs one failed staging attempt instead of the session.
 *
 * A failure staging one skill is caught per-directory, warned (path + message
 * only, never file contents), and skipped so one bad skill never aborts the scan.
 *
 * When `opts.declared` is provided, only skills whose dir name appears in it are
 * staged — undeclared on-disk dirs ("ghosts") are skipped so the effective set
 * equals `skaile.yaml` exactly (the canonical-contract invariant; the runner
 * always passes `declared`). Publisher-scope tolerance: `@pub/name` declared
 * matches a dir named `name`, and vice versa. When `opts.declared` is omitted the
 * scan stages every materialized skill (used by callers that do their own gating).
 *
 * @param projectDir - Workspace root containing `.skaile/assets/skill/`
 * @param skillsDir - Driver-relative skills dir, e.g. `.claude/skills`
 * @param opts - When `declared` is present, restrict staging to those names
 * @returns Names of the skills staged (empty if the source dir is absent)
 */
export async function stageMaterializedSkills(
  projectDir: string,
  skillsDir: string,
  opts?: { declared?: string[] },
): Promise<string[]> {
  const dir = join(projectDir, ".skaile/assets/skill");

  const staged: string[] = [];

  // One readdir replaces the former existsSync + readdirSync pair: a missing
  // dir and an unreadable dir are the same "nothing to stage" outcome, so the
  // separate probe bought nothing but a second trip to a possibly-wedged mount.
  let subdirs: string[];
  try {
    const entries = await withFsDeadline(`readdir ${dir}`, () =>
      readdirAsync(dir, { withFileTypes: true }),
    );
    subdirs = entries.filter((e) => e.isDirectory()).map((e) => e.name);
  } catch {
    return [];
  }

  // When a declared set is provided, build it once; each dir name is matched
  // against both its full form and its unscoped tail (publisher-scope tolerance).
  let declaredSet: Set<string> | undefined;
  if (opts?.declared !== undefined) {
    declaredSet = new Set<string>(opts.declared.flatMap((d) => [d, unscopedTail(d)]));
  }

  for (const name of subdirs) {
    const src = join(dir, name);

    // Declared-set filter first: it is free, and skipping it early spares an
    // undeclared ghost dir a filesystem round-trip we would then discard.
    if (declaredSet && !declaredSet.has(name) && !declaredSet.has(unscopedTail(name))) continue;

    const hasSkillMd = await tryWithFsDeadline(
      `stat ${join(src, "SKILL.md")}`,
      async () => {
        await statAsync(join(src, "SKILL.md"));
        return true;
      },
      false,
    );
    if (!hasSkillMd) continue;

    const dest = join(projectDir, skillsDir, name);
    try {
      // cp recursive creates dest; exclude `.instance.json` at any depth (resolvedSecrets).
      // A skill tree can carry bundled assets, so the copy gets a wider budget
      // than a single stat — scaled off the operator-tunable base deadline.
      await withFsDeadline(
        `cp ${src} -> ${dest}`,
        () =>
          cpAsync(src, dest, {
            recursive: true,
            force: true,
            filter: (source) => basename(source) !== ".instance.json",
          }),
        fsDeadlineMs() * TREE_COPY_DEADLINE_FACTOR,
      );
      staged.push(name);
    } catch (err) {
      // Never include file contents in the warning — only the failing path + message.
      console.warn(
        `[workspace-config] stageMaterializedSkills: failed to stage "${src}": ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  return staged;
}

/**
 * Resolve each `sources[]` entry to an on-disk clone path under the unified
 * machine cache (`<cache>/sources/<host>/<owner>/<repo>/`) and its resolved HEAD
 * commit. Entries with no on-disk clone are skipped — the install pipeline
 * clones them first.
 */
function buildSourceClones(sources: SourceEntry[]): SourceClone[] {
  const sourcesDir = getGlobalCacheDir();
  const clones: SourceClone[] = [];
  for (const s of sources) {
    const localPath = join(sourcesDir, sourceCacheKey(s.url));
    if (!existsSync(localPath)) continue;
    const commit = getRepoCommit(localPath) ?? "0".repeat(40);
    clones.push({ localPath, sourceUrl: s.url, commit, tag: s.pin });
  }
  return clones;
}

/** Build a synthetic CatalogEntry from a provenance candidate's MCP metadata. */
function catalogEntryFromCandidate(c: ProvenanceCandidate): CatalogEntry {
  const md = c.metadata ?? {};
  const entry: CatalogEntry = {
    name: c.name,
    kind: c.kind as CatalogEntry["kind"],
    description: c.description ?? (typeof md.description === "string" ? md.description : ""),
    source: c.files[0]?.path ?? "",
    publisher: c.publisher,
    version: c.version,
    requires: [],
    dependencies: c.deps ?? [],
    metadata: c.metadata,
  };
  if (c.category != null) entry.category = c.category;
  if (c.keywords && c.keywords.length > 0) entry.keywords = c.keywords;
  if (c.license != null) entry.license = c.license;
  if (c.homepage != null) entry.homepage = c.homepage;
  return entry;
}

/**
 * Load MCP server declarations by merging four sources:
 *
 * 1. **Catalog defaults** — `mcp-server:*` refs in the top-level `dependencies`
 *    array, resolved against the provenance index built from the project's
 *    cloned `sources[]`. The `MCP.md` frontmatter (captured on the walker's
 *    `metadata` field) provides default config (transport, command, args, env,
 *    url, headers).
 *
 * 2. **Store-staged assets** — MCP servers `skaile add mcp-server:<store-ref>`
 *    fetched into `<projectDir>/.skaile/cache/store/<publisher>/mcp-server/<name>/<version>/`
 *    (see {@link loadStoreStagedMcpDeclarations}). A store-sourced MCP has no
 *    clone to resolve against, so this is its only route into a session.
 *
 * 3. **Materialized assets** — MCP servers the platform materializer has written
 *    to `<projectDir>/.skaile/assets/mcp-server/<name>/MCP.md` for subscribed
 *    assets (see {@link loadMaterializedMcpDeclarations}). This scan ALWAYS runs,
 *    so a subscribed asset on disk with no `dependencies:` and no `mcp_servers:`
 *    entry is still picked up.
 *
 * 4. **Explicit declarations** — entries in the `mcp_servers` section of
 *    `skaile.yaml`. These override the other three sources field-by-field when
 *    the `id` matches.
 *
 * **Precedence (lowest → highest):** catalog-dep refs < store-staged assets <
 * materialized assets < explicit `mcp_servers:`. Same-id entries from different
 * sources merge (higher overlays onto lower) rather than double-counting.
 *
 * @param projectDir - Workspace root where `skaile.yaml` lives
 * @returns Unified array of `McpServerDeclaration` objects ready for the runner
 * @docLink packages/core/workspace-config#load-mcp-server-declarations
 */
/**
 * Resolve the first provenance candidate for an `mcp-server` ref. Prefers the
 * publisher-keyed candidate; falls back to any candidate whose key ends with
 * `/<kind>:<name>`. Returns `undefined` when nothing matches.
 */
function lookupMcpCandidate(
  provenanceIndex: ProvenanceIndex,
  ref: ReturnType<typeof parseAssetRef>,
): ProvenanceCandidate | undefined {
  let candidates: ProvenanceCandidate[] | undefined;
  if (ref.publisher) {
    candidates = provenanceIndex.get(`${ref.publisher}/${ref.kind}:${ref.name}`);
  }
  if (!candidates) {
    for (const [key, arr] of provenanceIndex) {
      if (key.endsWith(`/${ref.kind}:${ref.name}`)) {
        candidates = arr;
        break;
      }
    }
  }
  return candidates?.[0];
}

/**
 * Build the strict-mode declared-name set: explicit `mcp_servers` ids ∪ dep-ref
 * names (kind `mcp-server`), each contributing both its full and unscoped form
 * so a scoped declaration (e.g. id `@pub/excel`) still admits a materialized dir
 * named `excel`.
 */
function buildDeclaredMcpNames(
  explicit: McpServerDeclaration[],
  mcpRefStrings: string[],
): Set<string> {
  return new Set<string>([
    ...explicit.flatMap((e) => [e.id, unscopedTail(e.id)]),
    ...mcpRefStrings.flatMap((refStr) => {
      try {
        const r = parseAssetRef(refStr);
        return [r.name, unscopedTail(r.name)];
      } catch {
        return [];
      }
    }),
  ]);
}

/**
 * Collect `mcp-server` dep refs from the top-level `dependencies` array only.
 * (ai_resources[].dependencies is no longer a thing.) Parse failures surface
 * elsewhere (install pipeline) and are skipped here.
 */
function collectMcpRefStrings(dependencies: string[] | undefined): string[] {
  const mcpRefStrings: string[] = [];
  for (const dep of dependencies ?? []) {
    try {
      const ref = parseAssetRef(dep);
      if (ref.kind === "mcp-server") mcpRefStrings.push(dep);
    } catch {
      // Parse failures surface elsewhere (install pipeline). Skip here.
    }
  }
  return mcpRefStrings;
}

/** Build the provenance index from cloned sources for catalog-default lookup. */
async function buildMcpProvenanceIndex(
  sources: SourceEntry[],
  projectDir: string,
): Promise<ProvenanceIndex> {
  return sources.length > 0
    ? await buildProvenanceIndex(buildSourceClones(sources), { projectDir })
    : (new Map() as ProvenanceIndex);
}

/**
 * Source 1 (lowest): upsert catalog-dep refs resolved via the provenance index.
 */
function upsertCatalogMcpDecls(
  mcpRefStrings: string[],
  provenanceIndex: ProvenanceIndex,
  upsert: (decl: McpServerDeclaration) => void,
): void {
  const seen = new Set<string>();
  for (const refStr of mcpRefStrings) {
    let ref: ReturnType<typeof parseAssetRef>;
    try {
      ref = parseAssetRef(refStr);
    } catch {
      continue;
    }
    if (seen.has(ref.name)) continue;
    seen.add(ref.name);

    const candidate = lookupMcpCandidate(provenanceIndex, ref);
    if (!candidate) continue;

    const catalogDecl = mcpDeclFromCatalogEntry(catalogEntryFromCandidate(candidate));
    if (!catalogDecl) continue;

    upsert(catalogDecl);
  }
}

/**
 * Sources 2 and 3: upsert a batch of on-disk decls (store-staged, then
 * materialized), overlaying onto a matching catalog base. Only
 * `skaile.yaml`-declared assets may load (the canonical-contract invariant);
 * undeclared on-disk entries ("ghosts") are silently skipped so the effective set
 * equals skaile.yaml exactly. The platform always writes the full effective set,
 * so nothing legitimately active is dropped.
 */
function upsertGatedMcpDecls(
  decls: McpServerDeclaration[],
  explicit: McpServerDeclaration[],
  mcpRefStrings: string[],
  upsert: (decl: McpServerDeclaration) => void,
): void {
  const declaredNames = buildDeclaredMcpNames(explicit, mcpRefStrings);
  for (const decl of decls) {
    if (declaredNames.has(decl.id) || declaredNames.has(unscopedTail(decl.id))) upsert(decl);
  }
}

export async function loadMcpServerDeclarations(
  projectDir: string,
): Promise<McpServerDeclaration[]> {
  const config = resolveSkWorkspaceConfig(projectDir);
  const explicit = config.mcp_servers ?? [];

  // Materialized scan always runs — the primary use case is a subscribed asset
  // on disk with no dependencies: and no mcp_servers: entry.
  const materialized = loadMaterializedMcpDeclarations(projectDir);

  const mcpRefStrings = collectMcpRefStrings(config.dependencies);
  const provenanceIndex = await buildMcpProvenanceIndex(config.sources ?? [], projectDir);

  // byId tracks the current merged decl per id so later, higher-precedence
  // sources overlay onto it instead of double-counting.
  const byId = new Map<string, McpServerDeclaration>();
  const order: string[] = [];

  const upsert = (decl: McpServerDeclaration) => {
    const existing = byId.get(decl.id);
    if (existing) {
      byId.set(decl.id, mergeMcpDeclarations(existing, decl));
    } else {
      byId.set(decl.id, decl);
      order.push(decl.id);
    }
  };

  // Source 1 (lowest): catalog-dep refs resolved via the provenance index.
  upsertCatalogMcpDecls(mcpRefStrings, provenanceIndex, upsert);

  // Source 2: store-staged bytes (`skaile add mcp-server:<store-ref>`) — the only
  // route for a store-sourced MCP, which has no clone to resolve against.
  upsertGatedMcpDecls(loadStoreStagedMcpDeclarations(projectDir), explicit, mcpRefStrings, upsert);

  // Source 3: materialized assets — overlay onto matching catalog/staged base.
  // Only declared assets load (canonical-contract invariant); ghosts drop.
  upsertGatedMcpDecls(materialized, explicit, mcpRefStrings, upsert);

  // Source 4 (highest): explicit mcp_servers: — overlay field-by-field.
  for (const decl of explicit) {
    upsert(decl);
  }

  return order.map((id) => byId.get(id) as McpServerDeclaration);
}

// ── Agent definition resolver ──────────────────────────────────────────────────

/**
 * Walk upward from `from` (max 8 levels) until a directory containing
 * `ai-assets/` is found. Returns the absolute path to that `ai-assets/`
 * directory, or undefined if none found.
 */
function findAiAssetsDir(from: string): string | undefined {
  let dir = resolve(from);
  for (let i = 0; i < 8; i++) {
    const candidate = join(dir, "ai-assets");
    if (existsSync(candidate)) return candidate;
    const resolved = resolve(dir, "..");
    if (resolved === dir) break;
    dir = resolved;
  }
  return undefined;
}

/**
 * Resolve the `agent.definition` field from `skaile.yaml` in `projectDir`
 * to an absolute filesystem path.
 *
 * Resolution rules:
 *   - Absent or empty → undefined
 *   - Relative/absolute local path → resolve(projectDir, definition)
 *   - `ai-assets://<rest>` → walk up for ai-assets/, join with <rest>
 *   - `agent:<name>` catalog refs → unresolved here (returns undefined; the
 *     install pipeline materializes catalog agents into the workspace)
 *
 * Used by the runner to determine agentDir without requiring --agent-dir.
 * @docLink packages/core/workspace-config#resolve-agent-dir
 */
export function resolveAgentDir(projectDir: string): string | undefined {
  const config = resolveSkWorkspaceConfig(projectDir);
  const definition = config.agent?.definition;
  if (!definition) return undefined;

  // ai-assets:// URI
  if (definition.startsWith("ai-assets://")) {
    const relPath = definition.slice("ai-assets://".length);
    const aiAssetsDir = findAiAssetsDir(projectDir);
    if (!aiAssetsDir) return undefined;
    return join(aiAssetsDir, relPath);
  }

  // Catalog reference (`agent:<name>@<publisher>`) — not resolved by directory
  // scanning anymore; the install pipeline materializes catalog agents.
  if (definition.startsWith("agent:")) {
    return undefined;
  }

  // Local path (relative or absolute)
  return resolve(projectDir, definition);
}
